import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Send,
  AlertTriangle,
  ChevronRight,
  Clock,
  MessageSquare,
  Users,
  FileText,
  Video,
  Info,
  CheckCircle,
  Play,
  Mic,
  MicOff,
  Camera,
  CameraOff,
  DollarSign,
  Gamepad2,
  GripVertical,
  Check,
  X
} from 'lucide-react';
import api, { setInterviewInviteToken, apiErrorText } from '../../utils/api';
import { useTranslation } from 'react-i18next';
import VideoRecorder from '../../components/VideoRecorder';
import VideoConsentModal from '../../components/VideoConsentModal';
import SimulationAvatar, { AvatarEmotion } from '../../components/SimulationAvatar';
import SimulationEnvironment, { detectEnvironment } from '../../components/SimulationEnvironment';
import ScenarioIntro from '../../components/ScenarioIntro';
import WorkspaceSimulation from '../../components/WorkspaceSimulation';
import { tts } from '../../utils/tts';
import { stt } from '../../utils/stt';
import { resolveAssetUrl } from '../../utils/url';

// 3D scene is heavy (three.js); load it lazily only when the candidate enables it
const SimulationScene3D = React.lazy(() => import('../../components/SimulationScene3D'));

// Volume icon for TTS toggle
const VolumeIcon = ({ size = 18, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);

const VolumeOffIcon = ({ size = 18, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

const WEBGAZER_CDN_URL = 'https://cdn.jsdelivr.net/npm/webgazer@2.1.0/build/webgazer.min.js';

const ensureWebgazer = async (): Promise<WebGazerInstance | null> => {
  if (typeof window === 'undefined') return null;
  if ((window as any).webgazer) {
    return (window as any).webgazer as WebGazerInstance;
  }

  return new Promise((resolve) => {
    const onReady = () => resolve((window as any).webgazer || null);
    const onError = () => resolve(null);

    const existing = document.querySelector<HTMLScriptElement>('script[data-webgazer]');
    if (existing) {
      existing.addEventListener('load', onReady, { once: true });
      existing.addEventListener('error', onError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = WEBGAZER_CDN_URL;
    script.async = true;
    script.setAttribute('data-webgazer', 'true');
    script.addEventListener('load', onReady, { once: true });
    script.addEventListener('error', onError, { once: true });
    document.body?.appendChild(script);
  });
};

// Block type icons mapping
const BLOCK_ICONS: Record<string, any> = {
  qa: MessageSquare,
  hr_simulation: Users,
  case_study: FileText,
  video_qa: Video,
  video_simulation: Video,
  info: Info,
  salary_strategy: DollarSign,
  self_intro: Mic,
  interactive_game: Gamepad2
};

const normalizeBlockType = (blockType?: string): string => {
  if (!blockType) return '';
  if (blockType === 'simulation') return 'hr_simulation';
  return blockType;
};

interface FlowBlock {
  id: number;
  block_type: string;
  title: string;
  instructions: string;
  order: number;
  video_required: boolean;
  duration_minutes: number | null;
  config: Record<string, any>;
  rubric: Record<string, any>;
}

interface InterviewFlow {
  id: number;
  name: string;
  blocks: FlowBlock[];
  settings: Record<string, any>;
}

interface SimulationMessage {
  role: 'ai' | 'candidate';
  content: string;
  timestamp: Date;
  isIntroTranscript?: boolean;
}

const formatFileSize = (bytes?: number) => {
  if (!bytes || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getUploadName = (file: any) => {
  if (!file) return '';
  return file.original_name || file.filename || file.name || file.title || file.path || 'Dosya';
};

const getUploadUrl = (file: any) => {
  if (!file) return '';
  return file.url || file.path || file.file_url || '';
};

const getUploadExtension = (file: any) => {
  const raw = getUploadName(file) || getUploadUrl(file);
  const match = raw.toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
};

const renderUploadPreview = (file: any, resolvedUrl?: string) => {
  if (!resolvedUrl) return null;
  const ext = getUploadExtension(file);
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
    return (
      <img
        src={resolvedUrl}
        alt={getUploadName(file)}
        className="mt-2 w-full max-h-96 object-contain rounded-lg border border-gray-200"
        loading="lazy"
      />
    );
  }
  if (['mp4', 'webm', 'mov'].includes(ext)) {
    return (
      <video
        src={resolvedUrl}
        controls
        className="mt-2 w-full rounded-lg border border-gray-200"
      />
    );
  }
  if (ext === 'pdf') {
    return (
      <iframe
        src={resolvedUrl}
        title={getUploadName(file)}
        className="mt-2 w-full h-96 rounded-lg border border-gray-200"
      />
    );
  }
  return null;
};

const FlowInterviewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { interviewId } = useParams();
  const [searchParams] = useSearchParams();

  // The candidate endpoints are guarded by the invite token from the link: register it (together
  // with the interview id) before any request is made; api.ts attaches it as X-Invite-Token.
  setInterviewInviteToken(interviewId, searchParams.get('token'));

  // Interview state
  const [interview, setInterview] = useState<any>(null);
  const [flow, setFlow] = useState<InterviewFlow | null>(null);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [completed, setCompleted] = useState(false);
  const [aiLanguage, setAiLanguage] = useState<string>('tr');

  const isTurkish = i18n.language === 'tr';
  const isInterviewEnglish = aiLanguage === 'en';

  // Sync i18n language with interview AI language
  useEffect(() => {
    if (aiLanguage === 'en' && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    } else if (aiLanguage === 'tr' && i18n.language !== 'tr') {
      i18n.changeLanguage('tr');
    }
  }, [aiLanguage]);

  // Block-specific state
  const [blockStartTime, setBlockStartTime] = useState<number>(Date.now());
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<SimulationMessage[]>([]);
  const [blockCompleted, setBlockCompleted] = useState(false);
  const [caseStudyResponse, setCaseStudyResponse] = useState('');
  const [companyIntroText, setCompanyIntroText] = useState<string>('');
  const [salaryExpectationText, setSalaryExpectationText] = useState('');
  const [salaryCurrentSalaryText, setSalaryCurrentSalaryText] = useState('');
  const [showInstructions, setShowInstructions] = useState(true);

  // Video consent state
  const [showVideoConsent, setShowVideoConsent] = useState(false);
  const [videoConsentGiven, setVideoConsentGiven] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelledMessage, setCancelledMessage] = useState<string>('');
  const [isCancelled, setIsCancelled] = useState(false);

  // Video Q&A state
  const [videoQaBlob, setVideoQaBlob] = useState<Blob | null>(null);
  const [videoQaPreviewUrl, setVideoQaPreviewUrl] = useState<string | null>(null);
  const [videoQaUploading, setVideoQaUploading] = useState(false);
  const [videoQaMetadata, setVideoQaMetadata] = useState<{ audioOnly: boolean } | null>(null);

  // Self-introduction (audio-only) state
  const [selfIntroPayload, setSelfIntroPayload] = useState<any>(null);
  const [selfIntroBlob, setSelfIntroBlob] = useState<Blob | null>(null);
  const [selfIntroRecording, setSelfIntroRecording] = useState(false);
  const [selfIntroElapsed, setSelfIntroElapsed] = useState(0);
  const [selfIntroUploading, setSelfIntroUploading] = useState(false);
  const [introTranscript, setIntroTranscript] = useState<string | null>(null);
  const selfIntroRecorderRef = useRef<MediaRecorder | null>(null);
  const selfIntroChunksRef = useRef<Blob[]>([]);
  const selfIntroStreamRef = useRef<MediaStream | null>(null);
  const selfIntroTimerRef = useRef<number | null>(null);

  const [videoSimulationPayload, setVideoSimulationPayload] = useState<any>(null);
  const [workspaceTask, setWorkspaceTask] = useState<any>(null);
  const [workspaceSubmitting, setWorkspaceSubmitting] = useState(false);
  const [immersive3D, setImmersive3D] = useState(false);
  const [videoSimulationBlob, setVideoSimulationBlob] = useState<Blob | null>(null);
  const [videoSimulationPreviewUrl, setVideoSimulationPreviewUrl] = useState<string | null>(null);
  const [videoSimulationUploading, setVideoSimulationUploading] = useState(false);
  const [videoSimulationMetadata, setVideoSimulationMetadata] = useState<{ audioOnly: boolean } | null>(null);

  const [videoSimulationTextAnswer, setVideoSimulationTextAnswer] = useState('');
  const [aiVideoFallbackEnabled, setAiVideoFallbackEnabled] = useState(false);

  // Immersive simulation state
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [ttsSpeaking, setTtsSpeaking] = useState(false);
  const [avatarEmotion, setAvatarEmotion] = useState<AvatarEmotion>('neutral');
  const [aiTyping, setAiTyping] = useState(false);

  // STT state
  const [sttListening, setSttListening] = useState(false);
  const [sttInterim, setSttInterim] = useState('');
  const sttFinalRef = useRef('');

  const [aiEmotion, setAiEmotion] = useState<AvatarEmotion>('neutral');

  const aiVideoPollRef = useRef<number | null>(null);

  // Interactive game state
  const [gameSpec, setGameSpec] = useState<any>(null);
  const [gameAnswers, setGameAnswers] = useState<Record<string, string>>({});
  const [orderedItems, setOrderedItems] = useState<string[]>([]);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [gameResult, setGameResult] = useState<any>(null);
  const [gameFeedback, setGameFeedback] = useState<string>('');

  // Video state
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  const abandonSentRef = useRef(false);
  const interviewRef = useRef<any>(null);
  const completedRef = useRef(false);
  const currentBlockRef = useRef<FlowBlock | undefined>(undefined);
  const currentBlockIndexRef = useRef(0);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const hasStartedRef = useRef(false);
  const isAdvancingRef = useRef(false);

  const currentBlock = flow?.blocks[currentBlockIndex];

  useEffect(() => {
    interviewRef.current = interview;
  }, [interview]);

  useEffect(() => {
    completedRef.current = completed;
  }, [completed]);

  useEffect(() => {
    currentBlockRef.current = currentBlock;
  }, [currentBlock]);

  useEffect(() => {
    currentBlockIndexRef.current = currentBlockIndex;
  }, [currentBlockIndex]);

  useEffect(() => {
    mediaStreamRef.current = mediaStream;
  }, [mediaStream]);

  // TTS subscription
  useEffect(() => {
    const unsub = tts.subscribe((state) => {
      setTtsSpeaking(state === 'speaking');
      if (state === 'speaking') {
        setAvatarEmotion('speaking');
      } else if (avatarEmotion === 'speaking') {
        setAvatarEmotion('neutral');
      }
    });
    return () => {
      unsub();
      tts.stop();
    };
  }, []);

  // STT subscription
  useEffect(() => {
    const unsubState = stt.subscribe((state) => {
      setSttListening(state === 'listening');
    });
    const unsubResult = stt.onResult((transcript, isFinal) => {
      if (isFinal) {
        sttFinalRef.current += (sttFinalRef.current && !sttFinalRef.current.endsWith(' ') ? ' ' : '') + transcript;
        setInputMessage(sttFinalRef.current);
        setSttInterim('');
      } else {
        setSttInterim(transcript);
      }
    });
    return () => {
      unsubState();
      unsubResult();
      stt.stop();
    };
  }, []);

  // Stop TTS when block changes
  useEffect(() => {
    tts.stop();
    stt.stop();
    sttFinalRef.current = '';
    setSttInterim('');
    setAvatarEmotion('neutral');
    setAiEmotion('neutral');
    setAiTyping(false);
  }, [currentBlockIndex]);

  useEffect(() => {
    startInterview();
    const cleanupAntiCheating = setupAntiCheating();

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (completedRef.current) return;
      const currentInterview = interviewRef.current;
      if (!currentInterview || currentInterview.status !== 'in_progress') return;
      e.preventDefault();
      e.returnValue = '';
    };

    const handlePageHide = () => {
      if (completedRef.current) return;
      const currentInterview = interviewRef.current;
      if (!currentInterview || currentInterview.status !== 'in_progress') return;
      sendAbandonBeacon('pagehide');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      cleanupAntiCheating?.();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);

      const currentInterview = interviewRef.current;
      if (!completedRef.current && currentInterview && currentInterview.status === 'in_progress') {
        sendAbandonBeacon('unmount');
      }

      // Cleanup media stream
      const currentStream = mediaStreamRef.current;
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [interviewId]);

  useEffect(() => {
    const isVideoBlock =
      currentBlock?.block_type === 'video_qa' ||
      (currentBlock?.block_type === 'video_simulation' && currentBlock?.config?.workspace_enabled !== true);
    if (!isVideoBlock || !videoEnabled) return;
    if (typeof window === 'undefined') return;

    let cancelled = false;
    let gazeListener: ((data: any, timestamp: number) => void) | null = null;
    let webgazerInstance: WebGazerInstance | null = null;

    const startGazeTracking = async () => {
      const instance = await ensureWebgazer();
      if (cancelled || !instance) {
        return;
      }
      webgazerInstance = instance;

      gazeListener = (data: any) => {
        if (!data) return;
        const width = window.innerWidth || 1;
        const x = typeof data.x === 'number' ? data.x : null;
        if (x === null) return;
        const offscreen = x < width * 0.15 || x > width * 0.85;
        if (offscreen) {
          gazeOffscreenCountRef.current += 1;
        } else {
          gazeOffscreenCountRef.current = 0;
        }

        const now = Date.now();
        if (
          gazeOffscreenCountRef.current >= 6 &&
          now - lastGazeReportRef.current > 20000
        ) {
          lastGazeReportRef.current = now;
          gazeOffscreenCountRef.current = 0;
          reportSuspiciousActivity('gaze_offscreen', {
            threshold: 0.15,
            block_type: currentBlock?.block_type,
          });
        }
      };

      try {
        webgazerInstance.setGazeListener(gazeListener).begin();
        webgazerInstance.showVideo(false);
        webgazerInstance.showFaceOverlay(false);
        webgazerInstance.showFaceFeedbackBox(false);
      } catch (err) {
        console.warn('WebGazer failed to start:', err);
      }
    };

    startGazeTracking();

    return () => {
      cancelled = true;
      try {
        if (webgazerInstance) {
          if (gazeListener) {
            webgazerInstance.clearGazeListener();
          }
          webgazerInstance.pause();
        }
      } catch (err) {
        // ignore
      }
      gazeOffscreenCountRef.current = 0;
    };
  }, [currentBlock?.block_type, videoEnabled]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      if (videoQaPreviewUrl) {
        URL.revokeObjectURL(videoQaPreviewUrl);
      }
      if (videoSimulationPreviewUrl) {
        URL.revokeObjectURL(videoSimulationPreviewUrl);
      }

      if (aiVideoPollRef.current) {
        window.clearInterval(aiVideoPollRef.current);
        aiVideoPollRef.current = null;
      }
    };
  }, [videoQaPreviewUrl, videoSimulationPreviewUrl]);

  useEffect(() => {
    if (aiVideoPollRef.current) {
      window.clearInterval(aiVideoPollRef.current);
      aiVideoPollRef.current = null;
    }

    if (
      !interviewId ||
      !currentBlock ||
      (currentBlock.block_type !== 'video_simulation' && currentBlock.block_type !== 'hr_simulation')
    ) {
      return;
    }

    if (currentBlock?.config?.ai_video_enabled !== true) {
      return;
    }

    const initialStatus = String(videoSimulationPayload?.ai_video?.status || '').toLowerCase();
    if (!initialStatus || initialStatus === 'pending' || initialStatus === 'generating') {
      const poll = async () => {
        try {
          const res = await api.get(`/api/interviews/${interviewId}/block/${currentBlock.id}/ai-video`);
          setVideoSimulationPayload((prev: any) => {
            const base = prev && typeof prev === 'object' ? prev : {};
            return { ...base, ai_video: res.data };
          });
        } catch (e) {
          // ignore polling errors
        }
      };

      poll();
      aiVideoPollRef.current = window.setInterval(poll, 6000);
    }

    return () => {
      if (aiVideoPollRef.current) {
        window.clearInterval(aiVideoPollRef.current);
        aiVideoPollRef.current = null;
      }
    };
  }, [interviewId, currentBlock?.id, currentBlock?.block_type, currentBlock?.config?.ai_video_enabled, videoSimulationPayload?.ai_video?.status]);

  const aiVideoStatus = String(videoSimulationPayload?.ai_video?.status || '').toLowerCase();
  const aiVideoUrl = resolveAssetUrl(videoSimulationPayload?.ai_video?.video_url);
  const manualQuestions: string[] = Array.isArray(currentBlock?.config?.manual_questions)
    ? currentBlock?.config?.manual_questions || []
    : [];
  const contentUploads: any[] = Array.isArray(currentBlock?.config?.content_uploads)
    ? currentBlock?.config?.content_uploads || []
    : [];
  const videoSimulationMode =
    currentBlock?.block_type === 'video_simulation' && currentBlock?.config?.workspace_enabled !== true
      ? currentBlock?.config?.ai_video_enabled === true
        ? (['error', 'not_allowed', 'disabled'].includes(aiVideoStatus)
            ? (contentUploads.length > 0 || manualQuestions.length > 0 ? 'content' : 'scenario')
            : 'ai_video')
        : contentUploads.length > 0 || manualQuestions.length > 0
          ? 'content'
          : 'scenario'
      : null;
  const showVideoSimulationScenario = videoSimulationMode === 'scenario';
  const showVideoSimulationContent = videoSimulationMode === 'content';
  const showVideoSimulationAiVideo = videoSimulationMode === 'ai_video';
  const aiVideoIsBlocking =
    showVideoSimulationAiVideo && (!aiVideoStatus || aiVideoStatus === 'pending' || aiVideoStatus === 'generating');
  const explicitVideoSimulationQuestion = String(videoSimulationPayload?.question || '').trim();
  const hasExplicitVideoSimulationQuestion = manualQuestions.length > 0 || Boolean(explicitVideoSimulationQuestion);
  const videoSimulationFallbackQuestion = t(
    'flowInterview.videoSimulationFallbackQuestion');
  const videoSimulationResponseGuide = t(
    'flowInterview.videoSimulationResponseGuide');
  const shouldHideSimulationText =
    currentBlock?.block_type === 'hr_simulation' &&
    currentBlock?.config?.ai_video_enabled === true &&
    !aiVideoFallbackEnabled &&
    (!aiVideoStatus || aiVideoStatus === 'pending' || aiVideoStatus === 'generating' || aiVideoStatus === 'ready');
  const isSimulationInputDisabled =
    blockCompleted ||
    (currentBlock?.block_type === 'hr_simulation' &&
    currentBlock?.config?.ai_video_enabled === true &&
    !aiVideoFallbackEnabled &&
    (!aiVideoStatus || aiVideoStatus === 'pending' || aiVideoStatus === 'generating'));

  const aiVideoRequiresLoadingScreen =
    currentBlock?.block_type === 'hr_simulation' &&
    currentBlock?.config?.ai_video_enabled === true &&
    !aiVideoFallbackEnabled &&
    (!aiVideoStatus || aiVideoStatus === 'pending' || aiVideoStatus === 'generating');

  // 3D immersive mode is available only for classic role-play simulations
  // (not workspace, content-upload or AI-video variants)
  const canUse3DScene =
    currentBlock?.block_type === 'hr_simulation' &&
    currentBlock?.config?.workspace_enabled !== true &&
    currentBlock?.config?.ai_video_enabled !== true &&
    contentUploads.length === 0 &&
    manualQuestions.length === 0;
  const sceneEnvType = detectEnvironment(
    currentBlock?.config?.scenario_prompt,
    currentBlock?.config?.ai_persona,
    currentBlock?.config?.tags
  );
  const lastAiMessage = [...messages].reverse().find((m) => m.role !== 'candidate');

  // Timer effect (pause until AI video hazır)
  const shouldPauseTimer =
    (currentBlock?.block_type === 'hr_simulation' &&
      currentBlock?.config?.ai_video_enabled === true &&
      !aiVideoFallbackEnabled &&
      (!aiVideoStatus || aiVideoStatus === 'pending' || aiVideoStatus === 'generating')) ||
    (currentBlock?.block_type === 'video_simulation' && aiVideoIsBlocking);

  useEffect(() => {
    if (!currentBlock?.duration_minutes) {
      return;
    }

    if (showInstructions || shouldPauseTimer) {
      return;
    }

    if (timeRemaining === null) {
      setTimeRemaining(currentBlock.duration_minutes * 60);
      setBlockStartTime(Date.now());
    }

    const timer = window.setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 0) {
          window.clearInterval(timer);
          handleBlockTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [currentBlock?.duration_minutes, currentBlock?.id, showInstructions, shouldPauseTimer, timeRemaining]);

  const startInterview = async () => {
    try {
      abandonSentRef.current = false;
      const inviteToken = searchParams.get('token');
      const response = await api.post(`/api/interviews/${interviewId}/start-flow`, {
        invite_token: inviteToken || undefined
      });
      
      const fetchedFlow = response.data.flow;
      const normalizedFlow = fetchedFlow
        ? {
            ...fetchedFlow,
            blocks: Array.isArray(fetchedFlow.blocks)
              ? fetchedFlow.blocks.map((block: FlowBlock) => ({
                  ...block,
                  block_type: normalizeBlockType(block.block_type)
                }))
              : []
          }
        : null;

      setInterview(response.data.interview);
      setFlow(normalizedFlow);
      setCurrentBlockIndex(response.data.current_block_index || 0);
      setCompanyIntroText(response.data.company_intro_text || '');
      setAiLanguage(response.data.ai_language || 'tr');
      setError('');
    } catch (err: any) {
      console.error('Failed to start interview:', err);
      setError(apiErrorText(err?.response?.data?.detail) || t('interviewPage.startError'));
    } finally {
      setLoading(false);
    }
  };

  const submitVideoSimulationAnswer = async () => {
    if (!currentBlock || !videoSimulationBlob || sending || videoSimulationUploading) return;

    setVideoSimulationUploading(true);
    setSending(true);
    setAvatarEmotion('thinking');

    try {
      const responseTime = Math.round((Date.now() - blockStartTime) / 1000);
      const videoUrl = await uploadVideoSimulationBlob(videoSimulationBlob);
      if (!videoUrl) {
        alert(t('flowInterview.videoUploadFailed'));
        return;
      }

      const msgRes = await api.post(
        `/api/interviews/${interviewId}/block/${currentBlock.id}/message`,
        {
          content: JSON.stringify({ type: 'video_answer', video_url: videoUrl }),
          response_time_seconds: responseTime,
        }
      );

      setVideoSimulationBlob(null);
      setVideoSimulationPreviewUrl(null);

      // Multi-turn: check if AI returned a response for next turn
      const aiMessage = msgRes.data?.ai_message;
      if (aiMessage) {
        try {
          const turnPayload = JSON.parse(aiMessage);
          if (turnPayload.type === 'video_simulation_turn' && turnPayload.ai_message) {
            // Add AI character's response to messages
            setMessages(prev => [...prev, { role: 'ai', content: turnPayload.ai_message, timestamp: new Date() }]);
            const emotion = (turnPayload.emotion as AvatarEmotion) || 'neutral';
            setAvatarEmotion(emotion);
            setAiEmotion(emotion);
            // Speak the AI response if TTS is enabled
            speakAIMessage(turnPayload.ai_message);
          }
        } catch {
          // Not a JSON turn payload, treat as plain text
          setMessages(prev => [...prev, { role: 'ai', content: aiMessage, timestamp: new Date() }]);
          setAvatarEmotion('neutral');
          setAiEmotion('neutral');
          speakAIMessage(aiMessage);
        }
      }

      if (msgRes.data?.block_completed) {
        setBlockCompleted(true);
        setAvatarEmotion('happy');
        setAiEmotion('happy');
        setTimeout(() => moveToNextBlock(), 2000);
      }
    } catch (err) {
      console.error('Failed to submit video simulation answer:', err);
      alert(t('flowInterview.submitFailed'));
      setAvatarEmotion('neutral');
    } finally {
      setVideoSimulationUploading(false);
      setSending(false);
    }
  };

  const submitVideoSimulationTextAnswer = async () => {
    if (!currentBlock || sending) return;
    const text = (videoSimulationTextAnswer || '').trim();
    if (!text) return;

    setSending(true);
    setAvatarEmotion('thinking');
    try {
      const responseTime = Math.round((Date.now() - blockStartTime) / 1000);
      const msgRes = await api.post(
        `/api/interviews/${interviewId}/block/${currentBlock.id}/message`,
        {
          content: text,
          response_time_seconds: responseTime,
        }
      );

      setVideoSimulationTextAnswer('');

      // Multi-turn: check AI response
      const aiMessage = msgRes.data?.ai_message;
      if (aiMessage) {
        try {
          const turnPayload = JSON.parse(aiMessage);
          if (turnPayload.type === 'video_simulation_turn' && turnPayload.ai_message) {
            setMessages(prev => [...prev, { role: 'ai', content: turnPayload.ai_message, timestamp: new Date() }]);
            const emotion = (turnPayload.emotion as AvatarEmotion) || 'neutral';
            setAvatarEmotion(emotion);
            setAiEmotion(emotion);
            speakAIMessage(turnPayload.ai_message);
          }
        } catch {
          setMessages(prev => [...prev, { role: 'ai', content: aiMessage, timestamp: new Date() }]);
          setAvatarEmotion('neutral');
          setAiEmotion('neutral');
          speakAIMessage(aiMessage);
        }
      }

      if (msgRes.data?.block_completed) {
        setBlockCompleted(true);
        setAvatarEmotion('happy');
        setAiEmotion('happy');
        setTimeout(() => moveToNextBlock(), 2000);
      }
    } catch (err) {
      console.error('Failed to submit video simulation text answer:', err);
      alert(t('flowInterview.submitFailed'));
      setAvatarEmotion('neutral');
      setAiEmotion('neutral');
    } finally {
      setSending(false);
    }
  };

  const sendAbandonBeacon = (reason: string) => {
    if (abandonSentRef.current) return;
    if (!interviewId) return;
    if (!hasStartedRef.current) return;
    const inviteToken = searchParams.get('token');

    abandonSentRef.current = true;

    const payload = JSON.stringify({
      invite_token: inviteToken || undefined,
      reason,
      block_id: currentBlockRef.current?.id
    });

    const baseUrl = (api.defaults.baseURL || '').replace(/\/$/, '');
    const url = `${baseUrl}/api/interviews/${interviewId}/abandon`;

    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
        return;
      }
    } catch (err) {
      console.error('sendBeacon failed:', err);
    }

    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => undefined);
    } catch (err) {
      console.error('abandon fetch failed:', err);
    }
  };

  // Debounce tracking for anti-cheating - prevent duplicate reports
  const lastReportTimeRef = useRef<Record<string, number>>({});
  const lastHiddenAtRef = useRef<number>(0);
  const allowContentSwitchUntilRef = useRef<number>(0);
  const gazeOffscreenCountRef = useRef<number>(0);
  const lastGazeReportRef = useRef<number>(0);
  const REPORT_MIN_INTERVAL_MS: Record<string, number> = {
    focus_loss: 2000,
    tab_switch: 2000,
    copy_paste: 5000,
    window_blur: 2000,
  };

  const allowContentTabSwitch = (durationMs: number = 15000) => {
    allowContentSwitchUntilRef.current = Date.now() + durationMs;
  };

  const setupAntiCheating = () => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (Date.now() < allowContentSwitchUntilRef.current) return;
        lastHiddenAtRef.current = Date.now();
        reportSuspiciousActivity('tab_switch');
      }
    };

    const handleWindowBlur = () => {
      // Window blur covers Alt-Tab / switching windows where the tab may still be visible.
      // Avoid double-counting: if a visibilitychange (hidden) happened very recently,
      // treat it as a tab switch and skip blur.
      const now = Date.now();
      if (document.hidden) return;
      if (Date.now() < allowContentSwitchUntilRef.current) return;
      if (now - (lastHiddenAtRef.current || 0) < 600) return;
      reportSuspiciousActivity('window_blur');
    };

    const handlePaste = (e: ClipboardEvent) => {
      const pastedText = e.clipboardData?.getData('text') || '';
      if (pastedText.length > 20) {
        reportSuspiciousActivity('copy_paste', {
          pasted_length: pastedText.length,
          pasted_preview: pastedText.substring(0, 100)
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('paste', handlePaste);

    const screenCheckInterval = window.setInterval(() => {
      try {
        // @ts-ignore
        if (window.screen && 'isExtended' in window.screen) {
          // @ts-ignore
          if (window.screen.isExtended) {
            reportSuspiciousActivity('second_monitor');
          }
        }
      } catch (err) {
        // ignore
      }
    }, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('paste', handlePaste);
      window.clearInterval(screenCheckInterval);
    };
  };

  const reportSuspiciousActivity = async (type: string, metadata: Record<string, any> = {}) => {
    if (completedRef.current) return;
    const currentInterview = interviewRef.current;
    if (!currentInterview || currentInterview.status !== 'in_progress') return;

    if ((type === 'tab_switch' || type === 'window_blur') && Date.now() < allowContentSwitchUntilRef.current) {
      return;
    }

    // Throttle: avoid duplicate reports but don't undercount frequent tab switches
    const throttleKey = type === 'tab_switch' || type === 'window_blur' ? 'focus_loss' : type;
    const now = Date.now();
    const lastReport = lastReportTimeRef.current[throttleKey] || 0;
    const minInterval = REPORT_MIN_INTERVAL_MS[throttleKey] ?? 2000;
    if (now - lastReport < minInterval) {
      console.log(`Debounced ${type} report (${now - lastReport}ms since last)`);
      return;
    }
    lastReportTimeRef.current[throttleKey] = now;

    try {
      await api.post(`/api/interviews/${interviewId}/suspicious-activity`, {
        activity_type: type,
        activity_metadata: {
          timestamp: new Date().toISOString(),
          block_index: currentBlockIndexRef.current,
          block_type: currentBlockRef.current?.block_type,
          ...metadata
        }
      });
    } catch (err) {
      console.error('Failed to report suspicious activity:', err);
    }
  };

  const handleBlockTimeout = () => {
    // Auto-submit current block when time runs out
    if (currentBlock?.block_type === 'case_study') {
      submitCaseStudy();
    } else {
      moveToNextBlock();
    }
  };

  const startBlock = async () => {
    hasStartedRef.current = true;
    isAdvancingRef.current = false;
    setShowInstructions(false);
    setBlockStartTime(Date.now());
    setMessages([]);
    setInputMessage('');
    setBlockCompleted(false);
    setCaseStudyResponse('');
    setSalaryExpectationText('');
    setSalaryCurrentSalaryText('');
    setGameSpec(null);
    setGameAnswers({});
    setOrderedItems([]);
    setGameResult(null);
    setGameFeedback('');

    setVideoQaBlob(null);
    setVideoQaPreviewUrl(null);
    setVideoQaUploading(false);

    setVideoSimulationPayload(null);
    setVideoSimulationBlob(null);
    setVideoSimulationPreviewUrl(null);
    setVideoSimulationUploading(false);
    setVideoSimulationTextAnswer('');

    setSelfIntroPayload(null);
    setSelfIntroBlob(null);
    setSelfIntroRecording(false);
    setSelfIntroElapsed(0);
    setSelfIntroUploading(false);
    setIntroTranscript(null);

    setWorkspaceTask(null);
    setWorkspaceSubmitting(false);
    setImmersive3D(false);

    // Initialize block based on type
    if (
      currentBlock?.block_type === 'hr_simulation' ||
      currentBlock?.block_type === 'qa' ||
      currentBlock?.block_type === 'video_qa' ||
      currentBlock?.block_type === 'video_simulation' ||
      currentBlock?.block_type === 'self_intro'
    ) {
      // Get first AI message
      try {
        const response = await api.post(`/api/interviews/${interviewId}/block/${currentBlock.id}/start`);
        if (currentBlock?.block_type === 'self_intro') {
          // Self-intro start returns a JSON payload with intro config
          try {
            const payload = response.data.ai_message ? JSON.parse(response.data.ai_message) : null;
            setSelfIntroPayload(payload && payload.type === 'self_intro' ? payload : {});
          } catch {
            setSelfIntroPayload({});
          }
        } else if (currentBlock?.block_type === 'video_simulation' && currentBlock?.config?.workspace_enabled !== true) {
          if (response.data.ai_message) {
            try {
              const payload = JSON.parse(response.data.ai_message);
              setVideoSimulationPayload(payload);

              // Multi-turn: add opening question as AI message
              const maxTurns = Number(payload.max_turns) || 1;
              if (maxTurns > 1) {
                const openingQuestion = payload.question || payload.video_scenario || '';
                if (openingQuestion) {
                  setMessages([{ role: 'ai', content: openingQuestion, timestamp: new Date() }]);
                  setTimeout(() => speakAIMessage(openingQuestion), 300);
                }
              }
            } catch (e) {
              console.error('Failed to parse video simulation start payload:', e);
              setError(t('flowInterview.videoSimulationParseError'));
              return;
            }
          }
          if (response.data?.ai_video) {
            setVideoSimulationPayload((prev: any) => {
              const base = prev && typeof prev === 'object' ? prev : {};
              return { ...base, ai_video: response.data.ai_video };
            });
          }
        } else if (
          (currentBlock?.block_type === 'hr_simulation' || currentBlock?.block_type === 'video_simulation') &&
          currentBlock?.config?.workspace_enabled === true
        ) {
          // Workspace simulation: the start payload carries the generated task
          let parsedWorkspace: any = null;
          if (response.data.ai_message) {
            try {
              const payload = JSON.parse(response.data.ai_message);
              if (payload && payload.type === 'workspace_task' && payload.task) {
                parsedWorkspace = payload.task;
              }
            } catch (e) {
              console.error('Failed to parse workspace task payload:', e);
            }
          }
          if (parsedWorkspace) {
            setWorkspaceTask(parsedWorkspace);
          } else {
            setError(t('workspace.taskLoadError'));
          }
          return;
        } else {
          const serverMessages = Array.isArray(response.data.messages) ? response.data.messages : null;
          if (serverMessages && serverMessages.length) {
            setMessages(
              serverMessages
                .filter((m: any) => m && (m.role === 'ai' || m.role === 'candidate') && m.content)
                .map((m: any) => ({
                  role: m.role,
                  content: m.content,
                  timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
                }))
            );
          }
          if (currentBlock?.block_type === 'hr_simulation' && response.data?.ai_video) {
            setVideoSimulationPayload({ ai_video: response.data.ai_video });
          }
        }
        if (
          response.data.ai_message &&
          currentBlock?.block_type !== 'video_simulation' &&
          currentBlock?.block_type !== 'self_intro'
        ) {
          setMessages([{ role: 'ai', content: response.data.ai_message, timestamp: new Date() }]);
          // Speak the opening AI message
          setTimeout(() => speakAIMessage(response.data.ai_message), 300);
        }
      } catch (err) {
        console.error('Failed to start block:', err);
        if (
          (currentBlock?.block_type === 'hr_simulation' || currentBlock?.block_type === 'video_simulation') &&
          currentBlock?.config?.workspace_enabled === true
        ) {
          setError(t('workspace.taskLoadError'));
        }
      }
    }

    // Initialize interactive game block
    if (currentBlock?.block_type === 'interactive_game') {
      try {
        const response = await api.post(`/api/interviews/${interviewId}/block/${currentBlock.id}/start`);
        if (response.data.ai_message) {
          let gameData: any;
          try {
            gameData = JSON.parse(response.data.ai_message);
          } catch (e) {
            console.error('Failed to parse game start payload:', e);
            setError(t('flowInterview.gameStartParseError'));
            return;
          }
          if (gameData.game_spec) {
            setGameSpec(gameData.game_spec);
            // Initialize ordered items for ordering games
            if (gameData.game_spec.game_type === 'ordering' && gameData.game_spec.content?.items) {
              const shuffled = [...gameData.game_spec.content.items].sort(() => Math.random() - 0.5);
              setOrderedItems(shuffled.map((item: any) => item.id));
            } else if (
              gameData.game_spec.game_type === 'mixed' &&
              gameData.game_spec.content?.tasks?.ordering?.items
            ) {
              const shuffled = [...gameData.game_spec.content.tasks.ordering.items].sort(
                () => Math.random() - 0.5
              );
              setOrderedItems(shuffled.map((item: any) => item.id));
            }
          }
        }
      } catch (err) {
        console.error('Failed to start interactive game:', err);
      }
    }

    // Setup video if required
    if (
      currentBlock?.video_required &&
      currentBlock?.block_type !== 'video_qa' &&
      currentBlock?.block_type !== 'video_simulation'
    ) {
      // Video onam kontrolü
      const hasVideoConsent = localStorage.getItem('video-consent');
      if (!hasVideoConsent) {
        setShowVideoConsent(true);
        return;
      }
      await setupVideo();
    }

    // Video blokları için onam kontrolü (workspace modunda video kaydı yok)
    if (
      (currentBlock?.block_type === 'video_qa' || 
       (currentBlock?.block_type === 'video_simulation' && currentBlock?.config?.workspace_enabled !== true)) &&
      !videoConsentGiven
    ) {
      setShowVideoConsent(true);
      return;
    }
  };

  const handleVideoConsentAccept = () => {
    setVideoConsentGiven(true);
    setShowVideoConsent(false);
    localStorage.setItem('video-consent', 'accepted');
    localStorage.setItem('video-consent-date', new Date().toISOString());
    
    // Video kurulumunu başlat
    if (currentBlock?.video_required) {
      setupVideo();
    }
  };

  const handleVideoConsentReject = () => {
    setShowVideoConsent(false);
    setShowCancelConfirm(true);
  };

  const handleCandidateCancel = async () => {
    if (cancelSubmitting) return;
    setCancelSubmitting(true);
    try {
      const inviteToken = searchParams.get('token');
      await api.post(`/api/interviews/${interviewId}/abandon`, {
        invite_token: inviteToken || undefined,
        reason: 'candidate_cancelled'
      });
      setCancelledMessage(
        t(
          'flowInterview.cancelledByCandidate')
      );
      setShowCancelConfirm(false);
      setIsCancelled(true);
    } catch (err) {
      console.error('Failed to cancel interview:', err);
      setError(
        t(
          'flowInterview.cancelFailed')
      );
      setShowCancelConfirm(false);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const setupVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      setMediaStream(stream);
      setVideoEnabled(true);
      setAudioEnabled(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Failed to access camera:', err);
      // Don't set global error to avoid terminating the entire interview block.
      // Just disable video features and let the candidate continue with text/audio if possible.
      setVideoEnabled(false);
      setAudioEnabled(false);
      alert(t('flowInterview.cameraError'));
    }
  };

  const toggleVideo = () => {
    if (mediaStream) {
      mediaStream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setVideoEnabled(!videoEnabled);
    }
  };

  const toggleAudio = () => {
    if (mediaStream) {
      mediaStream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setAudioEnabled(!audioEnabled);
    }
  };

  const speakAIMessage = (text: string) => {
    if (!ttsEnabled || !text.trim()) return;
    const lang = isInterviewEnglish ? 'en' : 'tr';
    tts.speak(text, lang, {
      onEnd: () => setAvatarEmotion('neutral'),
    });
  };

  const submitWorkspaceSolution = async (submission: Record<string, any>) => {
    if (workspaceSubmitting || !currentBlock) return;
    setWorkspaceSubmitting(true);
    const responseTime = Math.round((Date.now() - blockStartTime) / 1000);
    try {
      const response = await api.post(`/api/interviews/${interviewId}/block/${currentBlock.id}/message`, {
        content: JSON.stringify({ type: 'workspace_submission', submission }),
        response_time_seconds: responseTime
      });
      if (response.data?.block_completed) {
        setBlockCompleted(true);
        setTimeout(() => moveToNextBlock(), 2500);
      }
    } catch (err) {
      console.error('Failed to submit workspace solution:', err);
      alert(t('workspace.submitError'));
      setWorkspaceSubmitting(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;

    const userMessage = inputMessage;
    const responseTime = Math.round((Date.now() - blockStartTime) / 1000);

    setInputMessage('');
    sttFinalRef.current = '';
    setSttInterim('');
    if (sttListening) stt.stop();
    setMessages(prev => [...prev, { role: 'candidate', content: userMessage, timestamp: new Date() }]);
    setSending(true);
    setAvatarEmotion('thinking');

    try {
      const response = await api.post(`/api/interviews/${interviewId}/block/${currentBlock?.id}/message`, {
        content: userMessage,
        response_time_seconds: responseTime
      });

      const { ai_message, block_completed, ai_emotion, ai_branch } = response.data;

      if (ai_message) {
        setMessages(prev => [...prev, { role: 'ai', content: ai_message, timestamp: new Date() }]);
        // Use emotion from backend if available
        const emotion = (ai_emotion as AvatarEmotion) || 'neutral';
        setAvatarEmotion(emotion);
        setAiEmotion(emotion);
        speakAIMessage(ai_message);
      } else {
        setAvatarEmotion('neutral');
        setAiEmotion('neutral');
      }

      if (block_completed) {
        setBlockCompleted(true);
        setAvatarEmotion('happy');
        setAiEmotion('happy');
        // Auto-advance after showing completion message
        setTimeout(() => moveToNextBlock(), 3000);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setAvatarEmotion('neutral');
    } finally {
      setSending(false);
    }
  };

  const uploadVideoQaBlob = async (blob: Blob) => {
    if (!currentBlock || !interviewId) return null;

    const isAudioOnly = blob.type.includes('audio');
    const file = new File([blob], isAudioOnly ? 'answer.webm' : 'answer.webm', { type: blob.type || 'video/webm' });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('audio_only', String(isAudioOnly));

    const uploadRes = await api.post(
      `/api/upload/video/${interviewId}/block/${currentBlock.id}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    const filePath = uploadRes?.data?.file_path;
    return typeof filePath === 'string' && filePath ? filePath : null;
  };

  const uploadVideoSimulationBlob = async (blob: Blob) => {
    if (!currentBlock || !interviewId) return null;

    const isAudioOnly = blob.type.includes('audio');
    const file = new File([blob], 'answer.webm', { type: blob.type || 'video/webm' });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('audio_only', String(isAudioOnly));

    const uploadRes = await api.post(
      `/api/upload/video/${interviewId}/block/${currentBlock.id}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    const filePath = uploadRes?.data?.file_path;
    return typeof filePath === 'string' && filePath ? filePath : null;
  };

  const submitVideoQaAnswer = async () => {
    if (!currentBlock || !videoQaBlob || sending || videoQaUploading) return;

    setVideoQaUploading(true);
    setSending(true);

    try {
      const responseTime = Math.round((Date.now() - blockStartTime) / 1000);
      const videoUrl = await uploadVideoQaBlob(videoQaBlob);
      if (!videoUrl) {
        alert(t('flowInterview.videoUploadFailed'));
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'candidate',
          content: t('flowInterview.videoAnswerSent'),
          timestamp: new Date(),
        },
      ]);

      const msgRes = await api.post(
        `/api/interviews/${interviewId}/block/${currentBlock.id}/message`,
        {
          content: JSON.stringify({ type: 'video_answer', video_url: videoUrl }),
          response_time_seconds: responseTime,
        }
      );

      const { ai_message, block_completed } = msgRes.data;
      if (ai_message) {
        setMessages((prev) => [
          ...prev,
          { role: 'ai', content: ai_message, timestamp: new Date() },
        ]);
      }

      setVideoQaBlob(null);
      setVideoQaPreviewUrl(null);

      if (block_completed) {
        setTimeout(() => moveToNextBlock(), 1500);
      }
    } catch (err) {
      console.error('Failed to submit video answer:', err);
      alert(t('flowInterview.submitFailed'));
    } finally {
      setVideoQaUploading(false);
      setSending(false);
    }
  };

  const stopSelfIntroRecording = () => {
    if (selfIntroTimerRef.current) {
      window.clearInterval(selfIntroTimerRef.current);
      selfIntroTimerRef.current = null;
    }
    if (selfIntroRecorderRef.current && selfIntroRecorderRef.current.state !== 'inactive') {
      selfIntroRecorderRef.current.stop();
    }
    selfIntroRecorderRef.current = null;
    setSelfIntroRecording(false);
  };

  const startSelfIntroRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      selfIntroStreamRef.current = stream;
      const mimeType =
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : '';
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      selfIntroChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) selfIntroChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(selfIntroChunksRef.current, {
          type: recorder.mimeType || 'audio/webm'
        });
        setSelfIntroBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
        selfIntroStreamRef.current = null;
      };
      recorder.start();
      selfIntroRecorderRef.current = recorder;
      setSelfIntroRecording(true);
      setSelfIntroElapsed(0);
      setSelfIntroBlob(null);
      const maxSec = Number(selfIntroPayload?.max_recording_seconds) || 120;
      selfIntroTimerRef.current = window.setInterval(() => {
        setSelfIntroElapsed((prev) => {
          const next = prev + 1;
          if (next >= maxSec) stopSelfIntroRecording();
          return next;
        });
      }, 1000);
    } catch (e) {
      console.error('Mic access failed:', e);
      alert(t('flowInterview.micAccessDenied'));
    }
  };

  const submitSelfIntro = async () => {
    if (!currentBlock || !selfIntroBlob || sending || selfIntroUploading) return;

    setSelfIntroUploading(true);
    setSending(true);

    try {
      const responseTime = Math.round((Date.now() - blockStartTime) / 1000);

      const file = new File([selfIntroBlob], 'intro.webm', {
        type: selfIntroBlob.type || 'audio/webm'
      });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('audio_only', 'true');

      const uploadRes = await api.post(
        `/api/upload/video/${interviewId}/block/${currentBlock.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const videoUrl = uploadRes?.data?.file_path;
      if (!videoUrl) {
        alert(t('flowInterview.videoUploadFailed'));
        return;
      }

      const msgRes = await api.post(
        `/api/interviews/${interviewId}/block/${currentBlock.id}/message`,
        {
          content: JSON.stringify({ type: 'intro_recording', video_url: videoUrl }),
          response_time_seconds: responseTime,
        }
      );

      const { ai_message, block_completed, intro_transcript } = msgRes.data;
      const transcript = typeof intro_transcript === 'string' ? intro_transcript : '';
      setIntroTranscript(transcript || '');
      setSelfIntroBlob(null);

      // Show the transcript as the candidate's message (readable, no audio player)
      setMessages((prev) => [
        ...prev,
        {
          role: 'candidate',
          content: transcript || t('flowInterview.introRecordedFallback'),
          isIntroTranscript: true,
          timestamp: new Date(),
        },
      ]);
      if (ai_message) {
        setMessages((prev) => [
          ...prev,
          { role: 'ai', content: ai_message, timestamp: new Date() },
        ]);
        speakAIMessage(ai_message);
      }

      if (block_completed) {
        setBlockCompleted(true);
        setTimeout(() => moveToNextBlock(), 2500);
      }
    } catch (err: any) {
      console.error('Failed to submit self-intro:', err);
      alert(apiErrorText(err?.response?.data?.detail) || t('flowInterview.submitFailed'));
    } finally {
      setSelfIntroUploading(false);
      setSending(false);
    }
  };

  const submitSalaryBlock = async () => {
    if (sending || !currentBlock) return;

    // Eğer maaş beklentisi soruluyorsa, boş bırakılamaz
    const askExpectation = currentBlock.config?.ask_expectation;
    const expectationTrimmed = salaryExpectationText.trim();
    if (askExpectation && !expectationTrimmed) {
      // Kullanıcıya basit bir uyarı göster
      alert(t('flowInterview.expectationRequired'));
      return;
    }

    setSending(true);
    try {
      const payload = {
        expectation_text: expectationTrimmed || null,
        current_salary_text: salaryCurrentSalaryText.trim() || null,
      };

      await api.post(`/api/interviews/${interviewId}/block/${currentBlock.id}/submit`, {
        response: JSON.stringify(payload),
        duration_seconds: Math.round((Date.now() - blockStartTime) / 1000),
      });

      moveToNextBlock();
    } catch (err) {
      console.error('Failed to submit salary block:', err);
    } finally {
      setSending(false);
    }
  };

  const submitCaseStudy = async () => {
    if (!caseStudyResponse.trim()) return;

    setSending(true);
    try {
      await api.post(`/api/interviews/${interviewId}/block/${currentBlock?.id}/submit`, {
        response: caseStudyResponse,
        duration_seconds: Math.round((Date.now() - blockStartTime) / 1000)
      });
      
      moveToNextBlock();
    } catch (err) {
      console.error('Failed to submit case study:', err);
    } finally {
      setSending(false);
    }
  };

  // Interactive game functions
  const handleMatchingAnswer = (itemId: string, targetId: string) => {
    setGameAnswers(prev => ({ ...prev, [itemId]: targetId }));
  };

  const handleTargetMatch = (targetId: string, itemId: string) => {
    setGameAnswers(prev => {
      const next = { ...prev };

      const matchingItemIds = new Set<string>(
        (gameSpec?.game_type === 'mixed'
          ? (gameSpec?.content?.tasks?.matching?.items || [])
          : (gameSpec?.content?.items || [])
        ).map((i: any) => i?.id).filter(Boolean)
      );

      Object.keys(next).forEach((k) => {
        if (matchingItemIds.size > 0 && !matchingItemIds.has(k)) return;
        if (next[k] === targetId) {
          delete next[k];
        }
      });

      if (itemId) {
        next[itemId] = targetId;
      }
      return next;
    });
  };

  const handleOrderChange = (fromIndex: number, toIndex: number) => {
    const newOrder = [...orderedItems];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setOrderedItems(newOrder);
  };

  const isGameReadyToSubmit = () => {
    if (!gameSpec) return false;

    if (gameSpec.game_type === 'mixed') {
      const tasks = gameSpec.content?.tasks;
      if (!tasks) return false;

      const matchingItems = Array.isArray(tasks.matching?.items) ? tasks.matching.items : [];
      const sortingItems = Array.isArray(tasks.sorting?.items) ? tasks.sorting.items : [];
      const scenarioSteps = Array.isArray(tasks.scenario?.steps) ? tasks.scenario.steps : [];
      const orderingItems = Array.isArray(tasks.ordering?.items) ? tasks.ordering.items : [];

      if (!matchingItems.length || !sortingItems.length || !scenarioSteps.length || !orderingItems.length) return false;

      const matchingSelectedTargets = matchingItems.map((i: any) => gameAnswers[i.id]).filter(Boolean);
      const matchingTargets = Array.isArray(tasks.matching?.targets) ? tasks.matching.targets : [];
      const matchingAnswered =
        matchingItems.length > 0 &&
        matchingTargets.length > 0 &&
        matchingSelectedTargets.length === matchingItems.length &&
        new Set(matchingSelectedTargets).size === matchingItems.length;

      const requiredAnswerIds = [...sortingItems.map((i: any) => i.id), ...scenarioSteps.map((s: any) => s.id)];
      const allAnswered = requiredAnswerIds.every((id: any) => Boolean(gameAnswers[id]));
      const orderingReady = orderedItems.length === orderingItems.length;
      return matchingAnswered && allAnswered && orderingReady;
    }

    if (gameSpec.game_type === 'ordering') {
      const items = Array.isArray(gameSpec.content?.items) ? gameSpec.content.items : [];
      return items.length > 0 && orderedItems.length === items.length;
    }

    if (gameSpec.game_type === 'matching') {
      const items = Array.isArray(gameSpec.content?.items) ? gameSpec.content.items : [];
      return items.length > 0 && items.every((i: any) => Boolean(gameAnswers[i.id]));
    }

    if (gameSpec.game_type === 'sorting') {
      const items = Array.isArray(gameSpec.content?.items) ? gameSpec.content.items : [];
      return items.length > 0 && items.every((i: any) => Boolean(gameAnswers[i.id]));
    }

    if (gameSpec.game_type === 'scenario') {
      const steps = Array.isArray(gameSpec.content?.steps) ? gameSpec.content.steps : [];
      return steps.length > 0 && steps.every((s: any) => Boolean(gameAnswers[s.id]));
    }

    return false;
  };

  const getGameCompletionStatus = () => {
    if (!gameSpec) {
      return {
        isReady: false,
        details: [t('flowInterview.loadingAssessment') as string],
      };
    }

    if (gameSpec.game_type !== 'mixed') {
      return {
        isReady: isGameReadyToSubmit(),
        details: [],
      };
    }

    const tasks = gameSpec.content?.tasks;
    if (!tasks) {
      return {
        isReady: false,
        details: [t('flowInterview.missingTasks') as string],
      };
    }

    const matchingItems = Array.isArray(tasks.matching?.items) ? tasks.matching.items : [];
    const sortingItems = Array.isArray(tasks.sorting?.items) ? tasks.sorting.items : [];
    const scenarioSteps = Array.isArray(tasks.scenario?.steps) ? tasks.scenario.steps : [];
    const orderingItems = Array.isArray(tasks.ordering?.items) ? tasks.ordering.items : [];

    const matchingAnswered = matchingItems.filter((i: any) => Boolean(gameAnswers[i.id])).length;
    const sortingAnswered = sortingItems.filter((i: any) => Boolean(gameAnswers[i.id])).length;
    const scenarioAnswered = scenarioSteps.filter((s: any) => Boolean(gameAnswers[s.id])).length;
    const orderingReady = orderedItems.length === orderingItems.length && orderingItems.length > 0;

    const details = [
      `${t('flowInterview.matchItems')}: ${matchingAnswered}/${matchingItems.length}`,
      `${t('flowInterview.sortItems')}: ${sortingAnswered}/${sortingItems.length}`,
      `${t('flowInterview.scenarioGame')}: ${scenarioAnswered}/${scenarioSteps.length}`,
      `${t('flowInterview.orderItems')}: ${orderingReady ? t('flowInterview.done') : t('flowInterview.pending')}`,
    ];

    return {
      isReady: isGameReadyToSubmit(),
      details,
    };
  };

  const submitGame = async () => {
    if (!currentBlock || sending) return;

    if (!isGameReadyToSubmit()) {
      alert(t('flowInterview.completeAllTasks'));
      return;
    }

    setSending(true);
    try {
      // Build game result based on game type
      let gameData: any = { type: 'submit' };

      if (gameSpec?.game_type === 'ordering') {
        gameData.order = orderedItems;
      } else if (gameSpec?.game_type === 'mixed') {
        gameData.answers = gameAnswers;
        gameData.order = orderedItems;
      } else {
        // matching / sorting / scenario
        gameData.answers = gameAnswers;
      }

      const response = await api.post(`/api/interviews/${interviewId}/block/${currentBlock.id}/message`, {
        content: JSON.stringify(gameData),
        response_time_seconds: Math.round((Date.now() - blockStartTime) / 1000)
      });

      if (response.data.block_completed) {
        if (response.data.game_evaluation) {
          setGameResult(response.data.game_evaluation);
        }
      }
    } catch (err) {
      console.error('Failed to submit game:', err);
      alert(t('flowInterview.submitFailed'));
    } finally {
      setSending(false);
    }
  };

  const moveToNextBlock = async () => {
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true;
    // Stop video if active
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
      setVideoEnabled(false);
      setAudioEnabled(false);
    }

    if (flow && currentBlockIndex < flow.blocks.length - 1) {
      setCurrentBlockIndex(prev => prev + 1);
      setShowInstructions(true);
      setTimeRemaining(null);
      
      // Update backend
      try {
        await api.post(`/api/interviews/${interviewId}/block/advance`);
      } catch (err) {
        console.error('Failed to advance block:', err);
        isAdvancingRef.current = false;
      }
    } else {
      // Interview completed
      completeInterview();
    }
  };

  const completeInterview = async () => {
    try {
      await api.post(`/api/interviews/${interviewId}/complete`);
      setCompleted(true);
    } catch (err) {
      console.error('Failed to complete interview:', err);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (
      !currentBlock ||
      currentBlock.block_type !== 'hr_simulation' ||
      currentBlock.config?.ai_video_enabled !== true
    ) {
      setAiVideoFallbackEnabled(false);
      return;
    }

    if (aiVideoStatus === 'error' || aiVideoStatus === 'disabled' || aiVideoStatus === 'not_allowed') {
      setAiVideoFallbackEnabled(true);
      return;
    }

    if (aiVideoStatus === 'pending' || aiVideoStatus === 'generating') {
      setAiVideoFallbackEnabled(false);
      const timer = window.setTimeout(() => {
        setAiVideoFallbackEnabled(true);
      }, 120000);
      return () => window.clearTimeout(timer);
    }

    setAiVideoFallbackEnabled(false);
  }, [currentBlock?.id, currentBlock?.block_type, currentBlock?.config?.ai_video_enabled, aiVideoStatus]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lotus-gold mx-auto mb-4"></div>
          <p className="text-gray-600">{t('flowInterview.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center space-y-4">
          <AlertTriangle size={48} className="mx-auto text-red-500" />
          <h2 className="text-2xl font-bold text-gray-900">{t('flowInterview.error')}</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Completed state
  if (isCancelled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <AlertTriangle size={64} className="mx-auto text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('flowInterview.cancelledTitle')}
          </h2>
          <p className="text-gray-600">
            {cancelledMessage || t('flowInterview.cancelledByCandidate')}
          </p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('flowInterview.completed')}
          </h2>
          <p className="text-gray-600">
            {t('flowInterview.completedMessage')}
          </p>
        </div>
      </div>
    );
  }

  if (!flow || !currentBlock) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card max-w-md wfull text-center">
          <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
          <p className="text-gray-600">{t('flowInterview.noFlow')}</p>
        </div>
      </div>
    );
  }

  const BlockIcon = BLOCK_ICONS[currentBlock.block_type] || MessageSquare;
  const hasSalaryQuestions = Boolean(
    currentBlock.config?.ask_expectation || currentBlock.config?.ask_current_salary
  );

  const videoSimulationMaxSeconds =
    Number(videoSimulationPayload?.max_recording_seconds) ||
    Number(currentBlock.config?.max_recording_seconds) ||
    120;
  const videoSimulationAllowRetake =
    (videoSimulationPayload?.allow_retake ?? currentBlock.config?.allow_retake) !== false;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BlockIcon size={24} className="text-lotus-gold" />
              <div>
                <h1 className="text-lg font-bold text-gray-900">{currentBlock.title}</h1>
                <p className="text-sm text-gray-500">
                  {t('flowInterview.blockProgress', {
                    current: currentBlockIndex + 1,
                    total: flow.blocks.length
                  })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Timer */}
              {timeRemaining !== null && (
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                  timeRemaining < 60 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  <Clock size={16} />
                  <span className="font-mono font-medium">{formatTime(timeRemaining)}</span>
                </div>
              )}

              {/* TTS Toggle - only for simulation blocks */}
              {(currentBlock.block_type === 'hr_simulation' || currentBlock.block_type === 'qa') && tts.isSupported() && (
                <button
                  onClick={() => {
                    const next = !ttsEnabled;
                    setTtsEnabled(next);
                    tts.setEnabled(next);
                    if (!next) tts.stop();
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    ttsEnabled
                      ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title={ttsEnabled ? t('flowInterview.ttsDisable') : t('flowInterview.ttsEnable')}
                >
                  {ttsSpeaking ? (
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                    </span>
                  ) : null}
                  {ttsEnabled ? <VolumeIcon size={16} /> : <VolumeOffIcon size={16} />}
                  <span className="hidden md:inline">
                    {ttsEnabled ? t('flowInterview.ttsOn') : t('flowInterview.ttsOff')}
                  </span>
                </button>
              )}

              {/* Anti-cheating warning */}
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <AlertTriangle size={16} />
                <span className="hidden md:inline">{t('flowInterview.monitoring')}</span>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-lotus-gold transition-all duration-300"
              style={{ width: `${((currentBlockIndex + 1) / flow.blocks.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Instructions overlay */}
      {showInstructions && currentBlock.block_type === 'hr_simulation' && currentBlock.config?.scenario_prompt && !currentBlock.config?.ai_video_enabled && currentBlock.config?.workspace_enabled !== true && (
        <ScenarioIntro
          title={currentBlock.title}
          instructions={currentBlock.instructions}
          durationMinutes={currentBlock.duration_minutes}
          scenarioPrompt={currentBlock.config?.scenario_prompt}
          scenarioContext={currentBlock.config?.scenario_context}
          aiPersona={currentBlock.config?.ai_persona}
          aiRole={currentBlock.config?.ai_role}
          blockConfig={currentBlock.config}
          onStart={startBlock}
        />
      )}

      {showInstructions && !(currentBlock.block_type === 'hr_simulation' && currentBlock.config?.scenario_prompt && !currentBlock.config?.ai_video_enabled && currentBlock.config?.workspace_enabled !== true) && (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="card max-w-2xl w-full">
            <div className="text-center mb-6">
              <div className={`inline-flex p-4 rounded-full mb-4 ${
                currentBlock.block_type === 'hr_simulation' ? 'bg-purple-100' :
                currentBlock.block_type === 'case_study' ? 'bg-amber-100' :
                currentBlock.block_type === 'video_qa' || currentBlock.block_type === 'video_simulation' ? 'bg-red-100' :
                'bg-blue-100'
              }`}>
                <BlockIcon size={32} className={
                  currentBlock.block_type === 'hr_simulation' ? 'text-purple-600' :
                  currentBlock.block_type === 'case_study' ? 'text-amber-600' :
                  currentBlock.block_type === 'video_qa' || currentBlock.block_type === 'video_simulation' ? 'text-red-600' :
                  'text-blue-600'
                } />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentBlock.title}</h2>
              {currentBlock.duration_minutes && (
                <p className="text-gray-500 flex items-center justify-center gap-2">
                  <Clock size={16} />
                  {t('flowInterview.duration', { minutes: currentBlock.duration_minutes })}
                </p>
              )}
            </div>

            {currentBlock.instructions && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-700 mb-2">{t('flowInterview.instructions')}</h3>
                <div className="text-gray-600 whitespace-pre-wrap">{currentBlock.instructions}</div>
              </div>
            )}

            {/* Block type specific info */}
            {(currentBlock.block_type === 'hr_simulation' || currentBlock.block_type === 'video_simulation') && currentBlock.config?.workspace_enabled === true && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-purple-800 mb-2">
                  {t('workspace.infoTitle')}
                </h3>
                <p className="text-purple-700 text-sm">
                  {t(
                    'workspace.infoDesc')}
                </p>
                <p className="text-purple-700 text-xs mt-3">
                  {t(
                    'flowInterview.abandonWarning')}
                </p>
              </div>
            )}

            {currentBlock.block_type === 'hr_simulation' && currentBlock.config?.workspace_enabled !== true && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-purple-800 mb-2">
                  {t('flowInterview.simulationInfo')}
                </h3>
                <p className="text-purple-700 text-sm">
                  {t(
                    'flowInterview.simulationDesc')}
                </p>
                <p className="text-purple-700 text-xs mt-3">
                  {t(
                    'flowInterview.abandonWarning')}
                </p>
              </div>
            )}

            {currentBlock.block_type === 'salary_strategy' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-800 mb-2">
                  {t('flowInterview.salaryInfoTitle')}
                </h3>
                <p className="text-green-700 text-sm">
                  {t(
                    'flowInterview.salaryInfoDesc')}
                </p>
              </div>
            )}

            {currentBlock.block_type === 'case_study' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-amber-800 mb-2">
                  {t('flowInterview.caseStudyInfo')}
                </h3>
                <p className="text-amber-700 text-sm">
                  {t('flowInterview.caseStudyDesc')}
                </p>
              </div>
            )}

            {currentBlock.block_type === 'interactive_game' && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-indigo-800 mb-2">
                  {t('flowInterview.interactiveGameInfo')}
                </h3>
                <p className="text-indigo-700 text-sm">
                  {t(
                    'flowInterview.interactiveGameDesc')}
                </p>
              </div>
            )}

            {currentBlock.video_required && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                  <Video size={18} />
                  {t('flowInterview.videoRequired')}
                </div>
                <p className="text-red-700 text-sm">
                  {t('flowInterview.videoRequiredDesc')}
                </p>
              </div>
            )}

            <button
              onClick={startBlock}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <Play size={20} />
              {t('flowInterview.startBlock')}
            </button>
          </div>
        </div>
      )}

      {/* Block content */}
      {!showInstructions && (
        <>
          {/* Video preview (if required) */}
          {currentBlock.video_required && currentBlock.block_type !== 'video_qa' && currentBlock.block_type !== 'video_simulation' && (
            <div className="bg-black p-2">
              <div className="max-w-4xl mx-auto flex items-center gap-4">
                <div className="relative w-32 h-24 bg-gray-900 rounded overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {!videoEnabled && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                      <CameraOff size={24} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={toggleVideo}
                    className={`p-2 rounded-full ${videoEnabled ? 'bg-gray-700 text-white' : 'bg-red-600 text-white'}`}
                  >
                    {videoEnabled ? <Camera size={18} /> : <CameraOff size={18} />}
                  </button>
                  <button
                    onClick={toggleAudio}
                    className={`p-2 rounded-full ${audioEnabled ? 'bg-gray-700 text-white' : 'bg-red-600 text-white'}`}
                  >
                    {audioEnabled ? <Mic size={18} /> : <MicOff size={18} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentBlock.block_type === 'video_simulation' && currentBlock.config?.workspace_enabled !== true && (
            <SimulationEnvironment
              blockConfig={currentBlock.config}
              scenarioPrompt={currentBlock.config?.scenario_description}
              aiPersona={currentBlock.config?.ai_persona}
            >
              <div className="flex-1 overflow-y-auto p-4">
                <div className="max-w-4xl mx-auto space-y-6">
                  {/* Multi-turn indicator */}
                  {Number(videoSimulationPayload?.max_turns) > 1 && !blockCompleted && (
                    <div className="flex items-center justify-center gap-2">
                      {Array.from({ length: Number(videoSimulationPayload?.max_turns) }).map((_, i) => {
                        const candidateTurns = messages.filter(m => m.role === 'candidate').length;
                        const turnStatus = i < candidateTurns ? 'done' : i === candidateTurns ? 'active' : 'pending';
                        return (
                          <div
                            key={`turn-dot-${i}`}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                              turnStatus === 'done'
                                ? 'bg-green-100 text-green-700'
                                : turnStatus === 'active'
                                ? 'bg-indigo-500 text-white shadow-md'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            {turnStatus === 'done' && <Check size={12} />}
                            {t('flowInterview.turn')} {i + 1}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Scenario card */}
                  <div className="card space-y-3">
                    <div className="text-sm font-medium text-gray-700">
                      {t('flowInterview.scenario')}
                    </div>
                    {showVideoSimulationScenario && (
                      <div className="text-gray-900 whitespace-pre-wrap">
                        {(videoSimulationPayload?.video_scenario as string) || t('flowInterview.loading')}
                      </div>
                    )}

                    {showVideoSimulationContent && (contentUploads.length > 0 || manualQuestions.length > 0) && (
                      <div className="pt-2 border-t border-gray-100 space-y-3">
                        {contentUploads.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-700">
                              {t('flowInterview.contentUploads')}
                            </div>
                            <div className="space-y-2">
                              {contentUploads.map((file: any, fileIndex: number) => {
                                const resolvedUrl = resolveAssetUrl(getUploadUrl(file));
                                const fileSize = formatFileSize(file?.size);
                                return (
                                  <div
                                    key={`video-simulation-upload-${fileIndex}`}
                                    className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
                                  >
                                    <div className="text-xs text-gray-700 truncate">
                                      <span className="font-medium">{getUploadName(file)}</span>
                                      {fileSize ? (
                                        <span className="ml-2 text-[11px] text-gray-500">({fileSize})</span>
                                      ) : null}
                                    </div>
                                    {resolvedUrl ? (
                                      <a
                                        href={resolvedUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => allowContentTabSwitch(20000)}
                                        className="text-xs text-blue-600 hover:underline"
                                      >
                                        {t('flowInterview.openFile')}
                                      </a>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {manualQuestions.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-700">
                              {t('flowInterview.manualQuestions')}
                            </div>
                            <ul className="list-decimal list-inside text-sm text-gray-800 space-y-1">
                              {manualQuestions.map((question: string, qIndex: number) => (
                                <li key={`video-simulation-question-${qIndex}`}>{question}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {showVideoSimulationAiVideo && (
                      <>
                        <div className="pt-2 border-t border-gray-100" />
                        <div className="text-sm font-medium text-gray-700">
                          {t('flowInterview.aiVideo')}
                        </div>
                        {aiVideoStatus === 'ready' && aiVideoUrl ? (
                          <video
                            src={aiVideoUrl}
                            controls
                            className="w-full rounded-lg border border-gray-200"
                          />
                        ) : aiVideoStatus === 'error' ? (
                          <div className="text-sm text-red-600">
                            {t('flowInterview.aiVideoError')}
                          </div>
                        ) : aiVideoStatus === 'not_allowed' || aiVideoStatus === 'disabled' ? (
                          <div className="text-sm text-gray-600">
                            {t('flowInterview.aiVideoDisabled')}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-600">
                            {t('flowInterview.aiVideoGenerating')}
                          </div>
                        )}
                      </>
                    )}

                    {/* First question (only show if no messages yet) */}
                    {messages.length === 0 && !aiVideoIsBlocking && (
                      <>
                        <div className="pt-2 border-t border-gray-100" />
                        <div className="text-sm font-medium text-gray-700">
                          {t('flowInterview.question')}
                        </div>
                        <div className="space-y-3">
                          <div className="text-gray-900 whitespace-pre-wrap">
                            {manualQuestions.length > 0
                              ? manualQuestions.map((question: string, qIndex: number) => (
                                  <div key={`video-simulation-question-line-${qIndex}`}>
                                    {qIndex + 1}. {question}
                                  </div>
                                ))
                              : (explicitVideoSimulationQuestion || videoSimulationFallbackQuestion)}
                          </div>
                          {!hasExplicitVideoSimulationQuestion && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                              <div className="font-medium">
                                {t('flowInterview.whatIsExpected')}
                              </div>
                              <div className="mt-1 whitespace-pre-wrap">
                                {videoSimulationResponseGuide}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Multi-turn conversation messages */}
                  {messages.length > 0 && (
                    <div className="space-y-4">
                      {messages.map((message, index) => {
                        const isAI = message.role !== 'candidate';
                        const isLastAI = isAI && index === messages.length - 1;
                        return (
                          <div key={index} className={`flex gap-3 ${isAI ? 'justify-start' : 'justify-end'}`}>
                            {isAI && (
                              <div className="flex-shrink-0 pt-1">
                                <SimulationAvatar
                                  emotion={isLastAI && ttsSpeaking ? 'speaking' : isLastAI && sending ? 'thinking' : 'neutral'}
                                  size="sm"
                                  speaking={isLastAI && ttsSpeaking}
                                />
                              </div>
                            )}
                            <div className={`max-w-2xl rounded-2xl p-4 ${isAI ? 'bg-white border border-gray-200 rounded-tl-sm' : 'bg-lotus-gold text-white rounded-tr-sm'}`}>
                              {isAI && (
                                <div className="text-xs font-medium text-gray-400 mb-1">
                                  {currentBlock.config?.ai_persona_name || t('flowInterview.aiCharacter')}
                                </div>
                              )}
                              <p className="whitespace-pre-wrap">{message.content}</p>
                              {isLastAI && ttsEnabled && tts.isSupported() && (
                                <button
                                  onClick={() => speakAIMessage(message.content)}
                                  className="mt-2 flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700"
                                >
                                  <VolumeIcon size={12} />
                                  {t('flowInterview.replay')}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {sending && (
                        <div className="flex gap-3 justify-start">
                          <div className="flex-shrink-0 pt-1">
                            <SimulationAvatar emotion="thinking" size="sm" />
                          </div>
                          <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm p-4">
                            <div className="flex gap-2">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Block completed message */}
                  {blockCompleted && (
                    <div className="card bg-green-50 border-green-200 text-center py-6">
                      <CheckCircle size={40} className="mx-auto text-green-500 mb-2" />
                      <p className="text-green-800 font-medium">
                        {t('flowInterview.simulationComplete')}
                      </p>
                    </div>
                  )}

                  {/* Video recording area (multi-turn: show if not completed) */}
                  {!aiVideoIsBlocking && currentBlock.video_required && !blockCompleted && (
                    <div className="card space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="font-semibold text-gray-900">
                          {messages.length > 0
                            ? t('flowInterview.recordNextAnswer')
                            : t('flowInterview.recordVideoAnswer')}
                        </h3>
                        <div className="text-xs text-gray-500">
                          {t('flowInterview.videoQaTip')}
                        </div>
                      </div>

                      {(videoSimulationAllowRetake || !videoSimulationBlob) && (
                        <VideoRecorder
                          onRecordingComplete={(blob, metadata) => {
                            setVideoSimulationBlob(blob);
                            setVideoSimulationMetadata(metadata || null);
                            const nextUrl = URL.createObjectURL(blob);
                            setVideoSimulationPreviewUrl(nextUrl);
                          }}
                          maxDuration={videoSimulationMaxSeconds}
                          showControls
                          className="w-full aspect-video"
                        />
                      )}

                      {videoSimulationPreviewUrl && (
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-gray-700">
                            {t('flowInterview.preview')}
                          </div>
                          <video
                            src={videoSimulationPreviewUrl}
                            controls
                            className="w-full rounded-lg border border-gray-200"
                          />
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={submitVideoSimulationAnswer}
                          disabled={sending || videoSimulationUploading || !videoSimulationBlob}
                          className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                          <Send size={18} />
                          {videoSimulationUploading
                            ? t('flowInterview.uploading')
                            : t('flowInterview.sendVideoAnswer')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Text answer fallback (multi-turn) */}
                  {!aiVideoIsBlocking && !currentBlock.video_required && !blockCompleted && (
                    <div className="card space-y-3">
                      <h3 className="font-semibold text-gray-900">
                        {t('flowInterview.yourResponse')}
                      </h3>
                      <textarea
                        value={videoSimulationTextAnswer}
                        onChange={(e) => setVideoSimulationTextAnswer(e.target.value)}
                        className="input min-h-[160px]"
                        placeholder={t('flowInterview.typePlaceholder') as string}
                        disabled={sending}
                      />
                      <div className="flex justify-end">
                        <button
                          onClick={submitVideoSimulationTextAnswer}
                          disabled={sending || !videoSimulationTextAnswer.trim()}
                          className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                          <Send size={18} />
                          {t('flowInterview.send')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </SimulationEnvironment>
          )}

          {/* Salary Strategy block */}
          {currentBlock.block_type === 'salary_strategy' && (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="card max-w-2xl w-full space-y-4">
                <p className="text-gray-700 text-sm">
                  {t(
                    'flowInterview.salaryBlockIntro')}
                </p>

                {currentBlock.config.show_range_to_candidate &&
                  typeof currentBlock.config.min_amount === 'number' &&
                  typeof currentBlock.config.max_amount === 'number' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h3 className="font-semibold text-green-800 mb-1">
                        {t('flowInterview.salaryRangeTitle')}
                      </h3>
                      <p className="text-green-800 text-sm">
                        {`${currentBlock.config.min_amount.toLocaleString(i18n.language === 'en' ? 'en-US' : 'tr-TR')} - ${currentBlock.config.max_amount.toLocaleString(i18n.language === 'en' ? 'en-US' : 'tr-TR')} ${currentBlock.config.currency || 'TRY'}`}
                      </p>
                      <p className="text-green-700 text-xs mt-2">
                        {t(
                          'flowInterview.salaryRangeNote')}
                      </p>
                    </div>
                  )}

                {hasSalaryQuestions && (
                  <p className="text-gray-600 text-xs">
                    {t(
                      'flowInterview.salaryQuestionsIntro')}
                  </p>
                )}

                {currentBlock.config?.ask_expectation && (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('flowInterview.expectationLabel')}
                    </label>
                    <textarea
                      value={salaryExpectationText}
                      onChange={(e) => setSalaryExpectationText(e.target.value)}
                      className="input min-h-[80px]"
                      placeholder={t(
                        'flowInterview.expectationPlaceholder') as string}
                    />
                  </div>
                )}

                {currentBlock.config?.ask_current_salary && (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('flowInterview.currentSalaryLabel')}
                    </label>
                    <textarea
                      value={salaryCurrentSalaryText}
                      onChange={(e) => setSalaryCurrentSalaryText(e.target.value)}
                      className="input min-h-[80px]"
                      placeholder={t(
                        'flowInterview.currentSalaryPlaceholder') as string}
                    />
                  </div>
                )}

                {!hasSalaryQuestions && (
                  <p className="text-gray-600 text-xs">
                    {t(
                      'flowInterview.salaryNoActionNeeded')}
                  </p>
                )}

                <button
                  onClick={hasSalaryQuestions ? submitSalaryBlock : moveToNextBlock}
                  disabled={sending}
                  className="btn-primary w-full mt-2 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {hasSalaryQuestions
                    ? t('flowInterview.submitAndContinue')
                    : t('flowInterview.continue')}
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Video Q&A block */}
          {currentBlock.block_type === 'video_qa' && (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Conversation (questions) */}
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'candidate' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-2xl rounded-lg p-4 ${
                          message.role === 'candidate'
                            ? 'bg-lotus-gold text-white'
                            : 'bg-white border border-gray-200'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recorder */}
                <div className="card space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-semibold text-gray-900">
                      {t('flowInterview.recordVideoAnswer')}
                    </h3>
                    <div className="text-xs text-gray-500">
                      {t('flowInterview.videoQaTip')}
                    </div>
                  </div>

                  <VideoRecorder
                    onRecordingComplete={(blob, metadata) => {
                      setVideoQaBlob(blob);
                      setVideoQaMetadata(metadata || null);
                      const nextUrl = URL.createObjectURL(blob);
                      setVideoQaPreviewUrl(nextUrl);
                    }}
                    maxDuration={Number(currentBlock.config?.max_duration_seconds) || 300}
                    showControls
                    className="w-full aspect-video"
                  />

                  {/* Preview */}
                  {videoQaPreviewUrl && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-700">
                        {t('flowInterview.preview')}
                      </div>
                      <video
                        src={videoQaPreviewUrl}
                        controls
                        className="w-full rounded-lg border border-gray-200"
                      />
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={submitVideoQaAnswer}
                      disabled={sending || videoQaUploading || !videoQaBlob}
                      className="btn-primary flex items-center gap-2 disabled:opacity-50"
                    >
                      <Send size={18} />
                      {videoQaUploading
                        ? t('flowInterview.uploading')
                        : t('flowInterview.sendVideoAnswer')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Self-introduction block (audio recording + transcript-grounded Q/A) */}
          {currentBlock.block_type === 'self_intro' && (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="max-w-4xl mx-auto space-y-6">
                {!introTranscript ? (
                  <div className="card space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Mic size={18} className="text-teal-600" />
                        {t('flowInterview.selfIntroTitle')}
                      </h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap mt-2">
                        {selfIntroPayload?.intro_prompt || t('flowInterview.selfIntroDefaultPrompt')}
                      </p>
                    </div>

                    {!selfIntroBlob ? (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={selfIntroRecording ? stopSelfIntroRecording : startSelfIntroRecording}
                          className={`btn-primary flex items-center gap-2 ${selfIntroRecording ? 'bg-red-600 hover:bg-red-700' : ''}`}
                        >
                          {selfIntroRecording ? <MicOff size={18} /> : <Mic size={18} />}
                          {selfIntroRecording
                            ? t('flowInterview.stopRecording')
                            : t('flowInterview.startRecording')}
                        </button>
                        {selfIntroRecording && (
                          <span className="text-sm text-red-600 animate-pulse font-medium">
                            ● {selfIntroElapsed}s / {Number(selfIntroPayload?.max_recording_seconds) || 120}s
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-600">
                          {t('flowInterview.introRecordedHint')}
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={submitSelfIntro}
                            disabled={selfIntroUploading || sending}
                            className="btn-primary flex items-center gap-2 disabled:opacity-50"
                          >
                            <Send size={18} />
                            {selfIntroUploading
                              ? t('flowInterview.uploading')
                              : t('flowInterview.sendIntro')}
                          </button>
                          {selfIntroPayload?.allow_retake !== false && (
                            <button
                              onClick={() => setSelfIntroBlob(null)}
                              disabled={selfIntroUploading || sending}
                              className="btn-secondary text-sm"
                            >
                              {t('flowInterview.retake')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Transcript + follow-up Q/A */}
                    <div className="space-y-4">
                      {messages.map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${message.role === 'candidate' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-2xl rounded-lg p-4 ${
                              message.role === 'candidate'
                                ? 'bg-lotus-gold text-white'
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            {(message as any).isIntroTranscript && (
                              <div className="text-xs opacity-75 mb-1 font-medium">
                                {t('flowInterview.introTranscriptLabel')}
                              </div>
                            )}
                            <p className="whitespace-pre-wrap">{message.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Text input for follow-up answers */}
                    <div className="flex gap-4 items-end">
                      <textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        placeholder={t('flowInterview.typePlaceholder')}
                        className="w-full input min-h-[100px]"
                        disabled={sending || blockCompleted}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={sending || blockCompleted || !inputMessage.trim()}
                        className="btn-primary flex items-center gap-2 flex-shrink-0"
                      >
                        <Send size={20} />
                        <span>{t('flowInterview.send')}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Workspace simulation block */}
          {(currentBlock.block_type === 'hr_simulation' || currentBlock.block_type === 'video_simulation') && currentBlock.config?.workspace_enabled === true && (
            blockCompleted ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="card max-w-md w-full text-center space-y-3">
                  <CheckCircle size={48} className="mx-auto text-green-500" />
                  <div className="text-sm font-semibold text-gray-900">
                    {t('workspace.submitted')}
                  </div>
                  <p className="text-xs text-gray-600">
                    {t('workspace.submittedMessage')}
                  </p>
                </div>
              </div>
            ) : !workspaceTask ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="card max-w-md w-full text-center space-y-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lotus-gold mx-auto"></div>
                  <div className="text-sm font-semibold text-gray-900">
                    {t('workspace.preparing')}
                  </div>
                  <p className="text-xs text-gray-600">
                    {t('workspace.preparingMessage')}
                  </p>
                </div>
              </div>
            ) : (
              <WorkspaceSimulation
                task={workspaceTask}
                submitting={workspaceSubmitting}
                onSubmit={submitWorkspaceSolution}
              />
            )
          )}

          {/* Q&A or Simulation block */}
          {(currentBlock.block_type === 'qa' || (currentBlock.block_type === 'hr_simulation' && currentBlock.config?.workspace_enabled !== true)) && (
            aiVideoRequiresLoadingScreen ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="card max-w-md w-full text-center space-y-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lotus-gold mx-auto"></div>
                  <div className="text-sm font-semibold text-gray-900">
                    {t('flowInterview.aiVideoGenerating')}
                  </div>
                  <p className="text-xs text-gray-600">
                    {t('flowInterview.aiVideoWaitMessage')}
                  </p>
                </div>
              </div>
            ) : (
              <SimulationEnvironment
                blockConfig={currentBlock.config}
                scenarioPrompt={currentBlock.config?.scenario_prompt}
                aiPersona={currentBlock.config?.ai_persona}
              >
                {canUse3DScene && (
                  <div className="absolute top-3 left-3 z-20">
                    <button
                      onClick={() => setImmersive3D((v) => !v)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs shadow-sm transition-colors ${
                        immersive3D
                          ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                          : 'bg-white/80 text-gray-600 hover:bg-white backdrop-blur-sm'
                      }`}
                      title={immersive3D
                        ? t('flowInterview.scene3DOff')
                        : t('flowInterview.scene3DOn')}
                    >
                      <span className="font-semibold">{immersive3D ? '2D' : '3D'}</span>
                      <span className="hidden sm:inline">
                        {immersive3D
                          ? t('flowInterview.scene3DChatLabel')
                          : t('flowInterview.scene3DSceneLabel')}
                      </span>
                    </button>
                  </div>
                )}
                {canUse3DScene && immersive3D ? (
                  <div className="flex-1 relative min-h-0 overflow-hidden">
                    <React.Suspense
                      fallback={
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lotus-gold"></div>
                        </div>
                      }
                    >
                      <div className="absolute inset-0">
                        <SimulationScene3D
                          sceneType={sceneEnvType}
                          emotion={ttsSpeaking ? 'speaking' : sending ? 'thinking' : aiEmotion}
                          speaking={ttsSpeaking}
                          characterName={currentBlock.config?.ai_persona_name || t('flowInterview.aiCharacter')}
                        />
                      </div>
                    </React.Suspense>
                    {/* Last AI message as subtitle */}
                    {lastAiMessage && (
                      <div className="absolute bottom-14 left-1/2 -translate-x-1/2 w-full max-w-2xl px-4 pointer-events-none">
                        <div className="bg-black/60 text-white text-sm rounded-2xl px-4 py-3 backdrop-blur-sm max-h-36 overflow-y-auto pointer-events-auto whitespace-pre-wrap">
                          {lastAiMessage.content}
                        </div>
                      </div>
                    )}
                    {sending && (
                      <div className="absolute bottom-4 right-4 flex gap-1.5 bg-black/50 rounded-full px-3 py-2 backdrop-blur-sm">
                        <div className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce"></div>
                        <div className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    )}
                  </div>
                ) : (
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="max-w-4xl mx-auto space-y-4">
                    {(currentBlock.block_type === 'hr_simulation' && (contentUploads.length > 0 || manualQuestions.length > 0)) && (
                      <div className="card space-y-3">
                        {contentUploads.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-700">
                              {t('flowInterview.contentUploads')}
                            </div>
                            <div className="space-y-2">
                              {contentUploads.map((file: any, fileIndex: number) => {
                                const resolvedUrl = resolveAssetUrl(getUploadUrl(file));
                                const fileSize = formatFileSize(file?.size);
                                return (
                                  <div
                                    key={`hr-simulation-upload-${fileIndex}`}
                                    className="rounded-lg border border-gray-200 bg-white px-3 py-2"
                                  >
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="text-xs text-gray-700 truncate">
                                        <span className="font-medium">{getUploadName(file)}</span>
                                        {fileSize ? (
                                          <span className="ml-2 text-[11px] text-gray-500">({fileSize})</span>
                                        ) : null}
                                      </div>
                                      {resolvedUrl ? (
                                        <a
                                          href={resolvedUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-xs text-blue-600 hover:underline"
                                        >
                                          {t('flowInterview.openFile')}
                                        </a>
                                      ) : null}
                                    </div>
                                    {renderUploadPreview(file, resolvedUrl)}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {manualQuestions.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-700">
                              {t('flowInterview.manualQuestions')}
                            </div>
                            <ul className="list-decimal list-inside text-sm text-gray-800 space-y-1">
                              {manualQuestions.map((question: string, qIndex: number) => (
                                <li key={`hr-simulation-question-${qIndex}`}>{question}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {currentBlock.block_type === 'hr_simulation' && currentBlock.config?.ai_video_enabled === true && (
                      <div className="card space-y-3">
                        <div className="text-sm font-medium text-gray-700">
                          {t('flowInterview.aiVideo')}
                        </div>
                        {aiVideoStatus === 'ready' && aiVideoUrl ? (
                          <video
                            src={aiVideoUrl}
                            controls
                            className="w-full rounded-lg border border-gray-200"
                          />
                        ) : aiVideoStatus === 'error' ? (
                          <div className="text-sm text-red-600">
                            {t('flowInterview.aiVideoError')}
                          </div>
                        ) : aiVideoStatus === 'not_allowed' || aiVideoStatus === 'disabled' ? (
                          <div className="text-sm text-gray-600">
                            {t('flowInterview.aiVideoDisabled')}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-600">
                            {t('flowInterview.aiVideoGenerating')}
                          </div>
                        )}
                      </div>
                    )}
                    {!shouldHideSimulationText && (
                      <>
                        {messages.map((message, index) => {
                          const isAI = message.role !== 'candidate';
                          const isLastAI = isAI && index === messages.length - 1;
                          const showAvatar = isAI && currentBlock.block_type === 'hr_simulation';
                          return (
                            <div
                              key={index}
                              className={`flex gap-3 ${isAI ? 'justify-start' : 'justify-end'}`}
                            >
                              {showAvatar && (
                                <div className="flex-shrink-0 pt-1">
                                  <SimulationAvatar
                                    emotion={isLastAI && ttsSpeaking ? 'speaking' : isLastAI && sending ? 'thinking' : isLastAI ? aiEmotion : 'neutral'}
                                    size="sm"
                                    speaking={isLastAI && ttsSpeaking}
                                  />
                                </div>
                              )}
                              <div
                                className={`max-w-2xl rounded-2xl p-4 ${
                                  isAI
                                    ? 'bg-white border border-gray-200 rounded-tl-sm'
                                    : 'bg-lotus-gold text-white rounded-tr-sm'
                                }`}
                              >
                                {showAvatar && isAI && (
                                  <div className="text-xs font-medium text-gray-400 mb-1">
                                    {currentBlock.config?.ai_persona_name || t('flowInterview.aiCharacter')}
                                  </div>
                                )}
                                <p className="whitespace-pre-wrap">{message.content}</p>
                                {isLastAI && ttsEnabled && tts.isSupported() && (
                                  <button
                                    onClick={() => speakAIMessage(message.content)}
                                    className="mt-2 flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700"
                                  >
                                    <VolumeIcon size={12} />
                                    {t('flowInterview.replay')}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {sending && (
                          <div className="flex gap-3 justify-start">
                            {currentBlock.block_type === 'hr_simulation' && (
                              <div className="flex-shrink-0 pt-1">
                                <SimulationAvatar emotion="thinking" size="sm" />
                              </div>
                            )}
                            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm p-4">
                              <div className="flex gap-2">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
                )}

                <div className="bg-white border-t border-gray-200 p-4">
                  <div className="max-w-4xl mx-auto">
                    {currentBlock.block_type === 'hr_simulation' && blockCompleted ? (
                      <button
                        onClick={moveToNextBlock}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                      >
                        {t('flowInterview.continue')}
                        <ChevronRight size={18} />
                      </button>
                    ) : (
                      <div className="space-y-2">
                        {/* STT interim text display */}
                        {sttListening && sttInterim && (
                          <div className="text-sm text-gray-400 italic px-2">
                            <span className="inline-flex items-center gap-1">
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                              </span>
                              {sttInterim}
                            </span>
                          </div>
                        )}
                        <div className="flex gap-4 items-end">
                          <div className="flex-1 relative">
                            <textarea
                              value={inputMessage}
                              onChange={(e) => {
                                setInputMessage(e.target.value);
                                sttFinalRef.current = e.target.value;
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  sendMessage();
                                }
                              }}
                              placeholder={sttListening
                                ? t('flowInterview.listeningPlaceholder')
                                : t('flowInterview.typePlaceholder')}
                              className={`w-full input min-h-[120px] ${sttListening ? 'ring-2 ring-red-300 border-red-300' : ''}`}
                              disabled={sending || isSimulationInputDisabled}
                            />
                          </div>
                          {/* STT microphone button */}
                          {stt.isSupported() && (
                            <button
                              onClick={() => {
                                if (sttListening) {
                                  stt.stop();
                                } else {
                                  sttFinalRef.current = inputMessage;
                                  stt.start(isInterviewEnglish ? 'en' : 'tr');
                                }
                              }}
                              disabled={sending || isSimulationInputDisabled}
                              className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full transition-all ${
                                sttListening
                                  ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={sttListening
                                ? t('flowInterview.sttStop')
                                : t('flowInterview.sttStart')}
                            >
                              {sttListening ? <MicOff size={20} /> : <Mic size={20} />}
                            </button>
                          )}
                          <button
                            onClick={sendMessage}
                            disabled={sending || isSimulationInputDisabled || !inputMessage.trim()}
                            className="btn-primary flex items-center gap-2 flex-shrink-0"
                          >
                            <Send size={20} />
                            <span>{t('flowInterview.send')}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </SimulationEnvironment>
            )
          )}

          {/* Case Study block */}
          {currentBlock.block_type === 'case_study' && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                {/* Case content */}
                {currentBlock.config.case_text && (
                  <div className="card mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FileText size={18} className="text-amber-600" />
                      {t('flowInterview.caseDescription')}
                    </h3>
                    <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                      {currentBlock.config.case_text}
                    </div>
                  </div>
                )}

                {/* Expected deliverables */}
                {currentBlock.config.expected_deliverables?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-amber-800 mb-2">
                      {t('flowInterview.expectedDeliverables')}
                    </h4>
                    <ul className="list-disc list-inside text-amber-700 text-sm space-y-1">
                      {currentBlock.config.expected_deliverables.map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Response area */}
                <div className="card">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {t('flowInterview.yourResponse')}
                  </h3>
                  <textarea
                    value={caseStudyResponse}
                    onChange={(e) => setCaseStudyResponse(e.target.value)}
                    className="input min-h-[300px]"
                    placeholder={t('flowInterview.caseResponsePlaceholder') as string}
                  />
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-gray-500">
                      {caseStudyResponse.length} {t('flowInterview.characters')}
                    </span>
                    <button
                      onClick={submitCaseStudy}
                      disabled={sending || !caseStudyResponse.trim()}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Send size={18} />
                      {t('flowInterview.submit')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info block */}
          {currentBlock.block_type === 'info' && (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="card max-w-2xl w-full">
                {currentBlock.config?.ai_intro_enabled === true && (
                  <div className="mb-6">
                    <div className="text-sm font-semibold text-gray-800 mb-2">
                      {t('flowInterview.aiIntroTitle')}
                    </div>
                    {companyIntroText ? (
                      <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                        {companyIntroText}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        {t('flowInterview.aiIntroEmpty')}
                      </p>
                    )}
                  </div>
                )}
                <div 
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: currentBlock.config.content || '' }}
                />
                <button
                  onClick={moveToNextBlock}
                  className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
                >
                  {t('flowInterview.continue')}
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Interactive Game block */}
          {currentBlock.block_type === 'interactive_game' && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                {/* Game Result Display */}
                {gameResult && (
                  <div className="card mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                    <div className="text-center">
                      <div className="text-xl font-bold text-indigo-700 mb-2">
                        {t('flowInterview.assessmentCompleted')}
                      </div>
                      <p className="text-gray-700">
                        {t(
                          'flowInterview.assessmentCompletedDesc')}
                      </p>

                      {typeof gameResult?.overall_score === 'number' && (
                        <div className="mt-4">
                          <div className="text-3xl font-bold text-indigo-700">
                            {gameResult.overall_score}/100
                          </div>
                          {(typeof gameResult?.correct_count === 'number' || typeof gameResult?.total_count === 'number') && (
                            <div className="text-sm text-gray-600 mt-1">
                              {t('flowInterview.assessmentAccuracy')}: {gameResult.correct_count ?? '-'} / {gameResult.total_count ?? '-'}
                            </div>
                          )}
                        </div>
                      )}

                      {(gameResult?.summary || gameResult?.scoring_explanation) && (
                        <div className="mt-4 text-left bg-white/70 border border-indigo-100 rounded-lg p-4">
                          {gameResult?.summary && (
                            <div className="text-sm text-gray-800">
                              <div className="font-semibold text-gray-900 mb-1">
                                {t('flowInterview.assessmentSummary')}
                              </div>
                              <div>{gameResult.summary}</div>
                            </div>
                          )}
                          {gameResult?.scoring_explanation && (
                            <div className="text-xs text-gray-600 mt-3">
                              {gameResult.scoring_explanation}
                            </div>
                          )}
                        </div>
                      )}

                      {gameResult?.task_breakdown && (
                        <div className="mt-4 text-left bg-white/70 border border-indigo-100 rounded-lg p-4">
                          <div className="font-semibold text-gray-900 mb-2">
                            {t('flowInterview.taskBreakdown')}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
                              <span className="text-gray-700">{t('flowInterview.matchItems')}</span>
                              <span className="font-semibold text-gray-900">
                                {gameResult.task_breakdown?.matching?.correct ?? 0}/{gameResult.task_breakdown?.matching?.total ?? 0}
                              </span>
                            </div>
                            <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
                              <span className="text-gray-700">{t('flowInterview.sortItems')}</span>
                              <span className="font-semibold text-gray-900">
                                {gameResult.task_breakdown?.sorting?.correct ?? 0}/{gameResult.task_breakdown?.sorting?.total ?? 0}
                              </span>
                            </div>
                            <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
                              <span className="text-gray-700">{t('flowInterview.scenarioGame')}</span>
                              <span className="font-semibold text-gray-900">
                                {gameResult.task_breakdown?.scenario?.correct ?? 0}/{gameResult.task_breakdown?.scenario?.total ?? 0}
                              </span>
                            </div>
                            <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
                              <span className="text-gray-700">{t('flowInterview.orderItems')}</span>
                              <span className="font-semibold text-gray-900">
                                {gameResult.task_breakdown?.ordering?.correct ?? 0}/{gameResult.task_breakdown?.ordering?.total ?? 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-6">
                        <button
                          onClick={moveToNextBlock}
                          className="btn-primary inline-flex items-center justify-center gap-2 px-8"
                        >
                          {t('flowInterview.continue')}
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Game Loading */}
                {!gameSpec && !gameResult && (
                  <div className="card text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('flowInterview.loadingAssessment')}</p>
                  </div>
                )}

                {/* Game Content */}
                {gameSpec && !gameResult && (
                  <div className="space-y-6">
                    {/* Game Title & Instructions */}
                    <div className="card">
                      <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <Gamepad2 className="text-indigo-600" size={24} />
                        {gameSpec.title}
                      </h2>
                    </div>

                    {/* Mixed (assessment pack) */}
                    {gameSpec.game_type === 'mixed' && gameSpec.content?.tasks && (
                      <div className="space-y-6">
                        {/* Matching */}
                        {Array.isArray(gameSpec.content.tasks.matching?.items) &&
                          gameSpec.content.tasks.matching.items.length > 0 &&
                          Array.isArray(gameSpec.content.tasks.matching?.targets) &&
                          gameSpec.content.tasks.matching.targets.length > 0 && (
                          <div className="card">
                            <h3 className="font-semibold text-gray-800 mb-4">
                              {t('flowInterview.matchItems')}
                            </h3>
                            <div className="space-y-3">
                              {gameSpec.content.tasks.matching.targets.map((target: any) => {
                                const selectedItemId =
                                  Object.keys(gameAnswers).find((k) => gameAnswers[k] === target.id) || '';
                                return (
                                  <div key={target.id} className="bg-white border-2 border-gray-200 rounded-lg p-3">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                                          {t('flowInterview.target')}
                                        </div>
                                        <div className="text-gray-800 text-sm leading-snug break-words">
                                          {target.text || t('flowInterview.missingTargetText')}
                                        </div>
                                      </div>
                                      <div className="w-full md:w-64">
                                        <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                                          {t('flowInterview.selectItem')}
                                        </div>
                                        <select
                                          value={selectedItemId}
                                          onChange={(e) => handleTargetMatch(target.id, e.target.value)}
                                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500"
                                          style={{ color: '#111827', backgroundColor: '#ffffff' }}
                                        >
                                          <option value="" className="text-gray-900" style={{ color: '#111827', backgroundColor: '#ffffff' }}>
                                            {t('flowInterview.selectMatch')}
                                          </option>
                                          {gameSpec.content.tasks.matching.items.map((item: any) => (
                                            <option
                                              key={item.id}
                                              value={item.id}
                                              className="text-gray-900"
                                              style={{ color: '#111827', backgroundColor: '#ffffff' }}
                                            >
                                              {item.text}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Ordering */}
                        {Array.isArray(gameSpec.content.tasks.ordering?.items) &&
                          gameSpec.content.tasks.ordering.items.length > 0 && (
                          <div className="card">
                            <h3 className="font-semibold text-gray-800 mb-4">
                              {t('flowInterview.orderItems')}
                            </h3>
                            <div className="space-y-2">
                              {orderedItems.map((itemId, index) => {
                                const item = gameSpec.content.tasks.ordering.items.find((i: any) => i.id === itemId);
                                if (!item) return null;
                                return (
                                  <div
                                    key={itemId}
                                    draggable
                                    onDragStart={() => setDraggedItem(itemId)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => {
                                      if (draggedItem && draggedItem !== itemId) {
                                        const fromIndex = orderedItems.indexOf(draggedItem);
                                        handleOrderChange(fromIndex, index);
                                      }
                                      setDraggedItem(null);
                                    }}
                                    className={`flex items-center gap-3 p-3 bg-white border-2 rounded-lg cursor-move transition-all ${
                                      draggedItem === itemId ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full font-bold">
                                      {index + 1}
                                    </div>
                                    <GripVertical className="text-gray-400" size={20} />
                                    <span className="flex-1 text-gray-800">{item.text}</span>
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-sm text-gray-500 mt-3">
                              {t('flowInterview.dragToReorder')}
                            </p>
                          </div>
                        )}

                        {/* Sorting */}
                        {Array.isArray(gameSpec.content.tasks.sorting?.items) &&
                          gameSpec.content.tasks.sorting.items.length > 0 &&
                          Array.isArray(gameSpec.content.tasks.sorting?.categories) &&
                          gameSpec.content.tasks.sorting.categories.length > 0 && (
                          <div className="card">
                            <h3 className="font-semibold text-gray-800 mb-4">
                              {t('flowInterview.sortItems')}
                            </h3>
                            <div className="space-y-3">
                              {gameSpec.content.tasks.sorting.items.map((item: any) => (
                                <div key={item.id} className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-lg">
                                  <span className="flex-1 text-gray-800">{item.text}</span>
                                  <select
                                    value={gameAnswers[item.id] || ''}
                                    onChange={(e) => handleMatchingAnswer(item.id, e.target.value)}
                                    className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500"
                                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                                  >
                                    <option value="" className="text-gray-900" style={{ color: '#111827', backgroundColor: '#ffffff' }}>
                                      {t('flowInterview.selectCategory')}
                                    </option>
                                    {gameSpec.content.tasks.sorting.categories.map((cat: any) => (
                                      <option
                                        key={cat.id}
                                        value={cat.id}
                                        className="text-gray-900"
                                        style={{ color: '#111827', backgroundColor: '#ffffff' }}
                                      >
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Scenario */}
                        {Array.isArray(gameSpec.content.tasks.scenario?.steps) &&
                          gameSpec.content.tasks.scenario.steps.length > 0 && (
                          <div className="card">
                            <h3 className="font-semibold text-gray-800 mb-4">
                              {t('flowInterview.scenarioGame')}
                            </h3>
                            {gameSpec.content.tasks.scenario.steps.map((step: any, stepIndex: number) => (
                              <div key={step.id} className={`mb-6 ${stepIndex > 0 ? 'border-t pt-6' : ''}`}>
                                <p className="text-gray-700 mb-4">{(step.text || '').trim() || t('flowInterview.scenarioStepFallback')}</p>
                                <div className="space-y-2">
                                  {step.choices?.map((choice: any) => (
                                    <button
                                      key={choice.id}
                                      onClick={() => handleMatchingAnswer(step.id, choice.id)}
                                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                        gameAnswers[step.id] === choice.id
                                          ? 'border-indigo-500 bg-indigo-50'
                                          : 'border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      {(choice.text || '').trim() || t('flowInterview.choiceFallback')}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Matching Game */}
                    {gameSpec.game_type === 'matching' && gameSpec.content?.items && gameSpec.content?.targets && (
                      <div className="card">
                        <h3 className="font-semibold text-gray-800 mb-4">
                          {t('flowInterview.matchItems')}
                        </h3>
                        <div className="space-y-3">
                          {gameSpec.content.targets.map((target: any) => {
                            const selectedItemId =
                              Object.keys(gameAnswers).find((k) => gameAnswers[k] === target.id) || '';
                            return (
                              <div key={target.id} className="bg-white border-2 border-gray-200 rounded-lg p-3">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                                      {t('flowInterview.target')}
                                    </div>
                                    <div className="text-gray-800 text-sm leading-snug break-words">
                                      {target.text || t('flowInterview.missingTargetText')}
                                    </div>
                                  </div>
                                  <div className="w-full md:w-64">
                                    <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                                      {t('flowInterview.selectItem')}
                                    </div>
                                    <select
                                      value={selectedItemId}
                                      onChange={(e) => handleTargetMatch(target.id, e.target.value)}
                                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500"
                                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                                    >
                                      <option value="" className="text-gray-900" style={{ color: '#111827', backgroundColor: '#ffffff' }}>
                                        {t('flowInterview.selectMatch')}
                                      </option>
                                      {gameSpec.content.items.map((item: any) => (
                                        <option
                                          key={item.id}
                                          value={item.id}
                                          className="text-gray-900"
                                          style={{ color: '#111827', backgroundColor: '#ffffff' }}
                                        >
                                          {item.text}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Ordering Game */}
                    {gameSpec.game_type === 'ordering' && gameSpec.content?.items && (
                      <div className="card">
                        <h3 className="font-semibold text-gray-800 mb-4">
                          {t('flowInterview.orderItems')}
                        </h3>
                        <div className="space-y-2">
                          {orderedItems.map((itemId, index) => {
                            const item = gameSpec.content.items.find((i: any) => i.id === itemId);
                            if (!item) return null;
                            return (
                              <div
                                key={itemId}
                                draggable
                                onDragStart={() => setDraggedItem(itemId)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                  if (draggedItem && draggedItem !== itemId) {
                                    const fromIndex = orderedItems.indexOf(draggedItem);
                                    handleOrderChange(fromIndex, index);
                                  }
                                  setDraggedItem(null);
                                }}
                                className={`flex items-center gap-3 p-3 bg-white border-2 rounded-lg cursor-move transition-all ${
                                  draggedItem === itemId ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full font-bold">
                                  {index + 1}
                                </div>
                                <GripVertical className="text-gray-400" size={20} />
                                <span className="flex-1 text-gray-800">{item.text}</span>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-sm text-gray-500 mt-3">
                          {t('flowInterview.dragToReorder')}
                        </p>
                      </div>
                    )}

                    {/* Sorting Game */}
                    {gameSpec.game_type === 'sorting' && gameSpec.content?.items && gameSpec.content?.categories && (
                      <div className="card">
                        <h3 className="font-semibold text-gray-800 mb-4">
                          {t('flowInterview.sortItems')}
                        </h3>
                        <div className="space-y-3">
                          {gameSpec.content.items.map((item: any) => (
                            <div key={item.id} className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-lg">
                              <span className="flex-1 text-gray-800">{item.text}</span>
                              <select
                                value={gameAnswers[item.id] || ''}
                                onChange={(e) => handleMatchingAnswer(item.id, e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500"
                                style={{ color: '#111827', backgroundColor: '#ffffff' }}
                              >
                                <option value="" className="text-gray-900" style={{ color: '#111827', backgroundColor: '#ffffff' }}>
                                  {t('flowInterview.selectCategory')}
                                </option>
                                {gameSpec.content.categories.map((cat: any) => (
                                  <option
                                    key={cat.id}
                                    value={cat.id}
                                    className="text-gray-900"
                                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                                  >
                                    {cat.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Scenario Game */}
                    {gameSpec.game_type === 'scenario' && gameSpec.content?.steps && (
                      <div className="card">
                        <h3 className="font-semibold text-gray-800 mb-4">
                          {t('flowInterview.scenarioGame')}
                        </h3>
                        {gameSpec.content.steps.map((step: any, stepIndex: number) => (
                          <div key={step.id} className={`mb-6 ${stepIndex > 0 ? 'border-t pt-6' : ''}`}>
                            <p className="text-gray-700 mb-4">{(step.text || '').trim() || t('flowInterview.scenarioStepFallback')}</p>
                            <div className="space-y-2">
                              {step.choices?.map((choice: any) => (
                                <button
                                  key={choice.id}
                                  onClick={() => handleMatchingAnswer(step.id, choice.id)}
                                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                    gameAnswers[step.id] === choice.id
                                      ? 'border-indigo-500 bg-indigo-50'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  {(choice.text || '').trim() || t('flowInterview.choiceFallback')}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end">
                      {(() => {
                        const status = getGameCompletionStatus();
                        const canSubmit = status.isReady;
                        return (
                          <button
                            onClick={submitGame}
                            disabled={sending}
                            aria-disabled={!canSubmit}
                            className={`btn-primary flex items-center gap-2 px-8 ${
                              !canSubmit ? 'opacity-60 cursor-not-allowed' : ''
                            }`}
                          >
                            {sending ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                {t('flowInterview.evaluating')}
                              </>
                            ) : (
                              <>
                                <Send size={18} />
                                {t('flowInterview.submitGame')}
                              </>
                            )}
                          </button>
                        );
                      })()}
                    </div>
                    {(() => {
                      const status = getGameCompletionStatus();
                      if (status.isReady || gameResult) return null;
                      return (
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>{t('flowInterview.submitHint')}</div>
                          {status.details.map((d: string, i: number) => (
                            <div key={i} className="text-gray-500">{d}</div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Video Consent Modal */}
      {showVideoConsent && (
        <VideoConsentModal
          isOpen={showVideoConsent}
          onClose={() => setShowVideoConsent(false)}
          onAccept={handleVideoConsentAccept}
          onCancel={handleVideoConsentReject}
          blockType={currentBlock?.block_type === 'video_qa' ? 'video_qa' : 'video_simulation'}
          interviewId={parseInt(interviewId || '0')}
          blockId={currentBlock?.id}
          inviteToken={searchParams.get('token')}
        />
      )}

      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-md w-full p-6 space-y-4">
            <div>
              <p className="text-sm uppercase tracking-widest text-amber-500 font-semibold">
                {t('flowInterview.cancelWarning')}
              </p>
              <h3 className="mt-2 text-2xl font-bold text-gray-900">
                {t('flowInterview.cancelConfirmTitle')}
              </h3>
              <p className="mt-2 text-gray-600">
                {t('flowInterview.cancelConfirmDesc')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 btn-outline"
                disabled={cancelSubmitting}
              >
                {t('flowInterview.cancelConfirmKeep')}
              </button>
              <button
                className="btn-primary"
                onClick={handleCandidateCancel}
                disabled={cancelSubmitting}
              >
                {cancelSubmitting
                  ? t('flowInterview.cancelConfirmProcessing')
                  : t('flowInterview.cancelConfirmConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowInterviewPage;
